import { useState, useRef, useEffect, useMemo, type MouseEvent } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { useRoute, useLocation, useSearch, Link } from "wouter";
import { useTabQueryParam } from "@/hooks/use-tab-query-param";
import { usePageTitle } from "@/hooks/use-page-title";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { Property, Room, ThirdPartyListing, MarketingSettings, CustomListingSite, ImagePrompt, MaintenanceTaskWithLinkedChats, TeamMember, Vendor } from "@shared/schema";
import AppLayout, { findOpenThermostatStuckSince, formatStuckDuration } from "@/components/app-layout";
import type { AccountType } from "@shared/schema";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { HouseGuideDocuments } from "./app-property-detail/HouseGuideDocuments";
import { ResidentSentiment } from "./app-property-detail/ResidentSentiment";
import { RoomVariantsDialog } from "./app-property-detail/RoomVariantsDialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Home, Plus, Trash2, ChevronRight, CalendarIcon, Upload, Image as ImageIcon, Save, Eye, Edit2, BookOpen, AlertTriangle, Building2, Link2, Link2Off, Copy, FileText, FileSearch, ExternalLink, ArrowUp, ArrowDown, ArrowUpDown, X, Megaphone, Thermometer, Lock as LockIcon, Battery, Wifi, WifiOff, Wrench, Info, SquareCheck } from "lucide-react";
import { ThermostatTrendChart } from "@/components/thermostat-trend-chart";
import { formatRelativeSync } from "@/lib/formatRelativeSync";
import { ISP_OPTIONS } from "@/lib/isp-utils";
import { PROPERTY_REDIRECT_TAB_TARGETS, PROPERTY_REDIRECT_ONLY_TABS } from "@/lib/property-redirect-tabs";
import { DOCUMENT_CATEGORY_LABELS, type DocumentCategory, type PropertyDocument } from "@shared/schema";
import { ManageDocumentDialog } from "@/components/manage-document-dialog";
import { AppliancesPanel, type ApplianceWithTickets } from "@/components/appliances-panel";
import { PropertySetupChecklist } from "./app-property-detail/PropertySetupChecklist";
import { InlinePromptSettingsTabs } from "./app-prompt-library";

type PropertyDocumentRow = Omit<PropertyDocument, "fileUrl"> & { vendorName: string | null; canDelete: boolean };
type PropertyRoom = Room & { hasSavedImages?: boolean };
import AddressAutocomplete from "@/components/address-autocomplete";
import PropertyMap from "@/components/property-map";
import { PropertyDetailGroup } from "@/components/property-detail-group";
import { ACCOUNT_TIMEZONES } from "@/lib/timezones";
import { guessTimezoneFromState } from "@shared/property-timezone";
import { format, parseISO } from "date-fns";
import ReactMarkdown from "react-markdown";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { ClosingTeamPanel } from "@/components/closing-team-panel";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { computeOccupancy } from "@shared/occupancy";
import { isPadsplitManagedRoom, PADSPLIT_ROOM_MANAGED_MESSAGE } from "@shared/room-management";

import { isPrimaryRoom } from "@shared/fees";
import { AIMemorySection } from "@/components/ai-memory-section";
import { AiMemoryEntriesPanel } from "@/components/ai-memory-entries-panel";
import { PROPERTY_IMAGE_MAX_BYTES, PROPERTY_IMAGE_MIME_TYPES, isSafePropertyVideoUrl } from "@shared/property-media";
import { useHomeCardVisibility } from "@/hooks/use-home-card-visibility";

const PRIORITY_COLORS: Record<string, string> = {
  urgent: "bg-red-100 text-red-800",
  high: "bg-orange-100 text-orange-800",
  medium: "bg-yellow-100 text-yellow-800",
  low: "bg-green-100 text-green-800",
};

type ChecklistReadState = "loading" | "success" | "error" | "restricted";
function getChecklistReadState(query: { isLoading: boolean; isSuccess: boolean; error: unknown }): ChecklistReadState {
  if (query.isLoading) return "loading";
  if (query.isSuccess) return "success";
  const status = (query.error as { status?: number } | null)?.status;
  return status === 403 ? "restricted" : "error";
}

function responseError(status: number, message: string): Error & { status: number } {
  return Object.assign(new Error(message), { status });
}

// Task #2223 — Mirror the estimate/final badge logic from
// `client/src/pages/app-tasks.tsx` TaskCard so hosts viewing a single
// property's task list see the same vendor-estimate signal as the
// Operations/Maintenance dashboard.
function PropertyMaintenanceTasksPanel({ propertyId }: { propertyId: number }) {
  const tasksQuery = useQuery<MaintenanceTaskWithLinkedChats[]>({
    queryKey: ["/api/app/tasks"],
  });
  // Task #2241 — resolve assignee/vendor display names inline. Reuse the
  // same endpoints the Operations Maintenance page (`app-tasks.tsx`) uses
  // so we don't add a per-row round-trip.
  const teamQuery = useQuery<TeamMember[]>({ queryKey: ["/api/app/team-members"] });
  const vendorsQuery = useQuery<Vendor[]>({ queryKey: ["/api/app/operations/vendors"] });
  const teamById = new Map<number, TeamMember>();
  for (const m of teamQuery.data ?? []) teamById.set(m.id, m);
  const vendorById = new Map<number, Vendor>();
  for (const v of vendorsQuery.data ?? []) vendorById.set(v.id, v);
  const tasks = (tasksQuery.data ?? [])
    .filter((t) => t.propertyId === propertyId)
    .sort((a, b) => {
      // Open tasks first, then by createdAt desc within each group.
      const aDone = a.status === "complete" ? 1 : 0;
      const bDone = b.status === "complete" ? 1 : 0;
      if (aDone !== bDone) return aDone - bDone;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

  return (
    <div className="border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Maintenance Tasks</h3>
        <Link href={`/app/tasks?propertyId=${propertyId}`}>
          <Button variant="outline" size="sm" data-testid="button-open-maintenance-board">
            <Wrench className="h-3.5 w-3.5 mr-1.5" />
            Open in Maintenance
          </Button>
        </Link>
      </div>
      {tasksQuery.isLoading ? (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      ) : tasks.length === 0 ? (
        <div className="text-center py-10 text-sm text-gray-400" data-testid="text-no-property-tasks">
          No maintenance tasks for this property yet.
        </div>
      ) : (
        <div className="space-y-2" data-testid="list-property-tasks">
          {tasks.map((task) => {
            const isOverdue = task.dueDate != null && task.status !== "complete" && new Date(task.dueDate) < new Date();
            const assignee = task.assigneeId != null ? teamById.get(task.assigneeId) : null;
            const vendor = task.vendorId != null ? vendorById.get(task.vendorId) : null;
            const assigneeName = assignee ? (assignee.name || assignee.email || null) : null;
            const vendorName = vendor ? (vendor.businessName || vendor.name || null) : null;
            return (
              <Link key={task.id} href={`/app/tasks?task=${task.id}`}>
                <div
                  className="flex items-start justify-between gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50 hover:bg-gray-100 hover:shadow-sm transition-all cursor-pointer"
                  data-testid={`row-property-task-${task.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                      <span className="text-[11px] text-gray-400" data-testid={`text-task-id-${task.id}`}>#{task.id}</span>
                      <Badge className={`text-xs h-5 py-0 ${PRIORITY_COLORS[task.priority] ?? "bg-gray-100 text-gray-800"}`} data-testid={`badge-priority-${task.id}`}>
                        {task.priority}
                      </Badge>
                      <span className="text-xs text-gray-500 capitalize" data-testid={`text-task-status-${task.id}`}>
                        {task.status.replace(/_/g, " ")}
                      </span>
                      {task.dueDate && (
                        <span className={`text-xs ${isOverdue ? "text-red-600 font-medium" : "text-gray-400"}`}>
                          · Due {format(new Date(task.dueDate), "MMM d")}
                        </span>
                      )}
                      {task.status !== "complete" && task.lockedRateType && task.lockedRateCents != null && task.lockedRateCents > 0 && (() => {
                        const isHourly = task.lockedRateType === "hourly";
                        const travel = task.travelFeeCents ?? 0;
                        const labor = isHourly ? (task.estimateCents ?? 0) : (task.lockedRateCents ?? 0);
                        const total = labor + travel;
                        if (total <= 0) return null;
                        const formatted = `$${(total / 100).toFixed(2)}`;
                        const label = isHourly ? `~${formatted} est.` : `${formatted} est.`;
                        const tooltip = isHourly
                          ? `Vendor estimate (hourly): ${formatted}${task.estimateHours ? ` for ~${task.estimateHours} hrs` : ""}${travel > 0 ? ` incl. $${(travel / 100).toFixed(2)} travel` : ""}. Final charge depends on actual hours worked.`
                          : `Vendor estimate (flat): ${formatted}${travel > 0 ? ` incl. $${(travel / 100).toFixed(2)} travel` : ""}.`;
                        return (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                variant="outline"
                                className="text-xs border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300"
                                data-testid={`badge-estimate-${task.id}`}
                              >
                                {label}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>{tooltip}</TooltipContent>
                          </Tooltip>
                        );
                      })()}
                      {task.status === "complete" && task.priceCents != null && task.priceCents > 0 && (
                        <Badge
                          variant="outline"
                          className="text-xs border-green-200 bg-green-50 text-green-700"
                          data-testid={`badge-final-${task.id}`}
                        >
                          ${(task.priceCents / 100).toFixed(2)}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm font-medium text-gray-900 line-clamp-1" data-testid={`text-task-title-${task.id}`}>
                      {task.title}
                    </p>
                    {task.description && (
                      <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">{task.description}</p>
                    )}
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
                      {assigneeName ? (
                        <span data-testid={`text-task-assignee-${task.id}`}>
                          <span className="text-gray-400">Assigned:</span> {assigneeName}
                        </span>
                      ) : null}
                      {vendorName ? (
                        <span data-testid={`text-task-vendor-${task.id}`}>
                          <span className="text-gray-400">Vendor:</span> {vendorName}
                        </span>
                      ) : null}
                      {!assigneeName && !vendorName ? (
                        <span className="text-gray-400" data-testid={`text-task-unassigned-${task.id}`}>
                          Unassigned
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DatePickerField({ label, value, onChange, testId }: {
  label: string; value: string; onChange: (val: string) => void; testId: string;
}) {
  const [open, setOpen] = useState(false);
  let dateValue: Date | undefined;
  if (value) {
    try {
      const parsed = parseISO(value);
      if (!isNaN(parsed.getTime())) dateValue = parsed;
    } catch { /* ignore invalid dates */ }
  }

  return (
    <div>
      <label className="block text-xs font-semibold text-gray-700 mb-1">{label}</label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-start text-left font-normal text-sm h-10"
            data-testid={testId}
          >
            <CalendarIcon className="mr-2 h-4 w-4 text-gray-400" />
            {dateValue ? format(dateValue, "MMM d, yyyy") : <span className="text-gray-400">Pick a date</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={dateValue}
            defaultMonth={dateValue}
            onSelect={(date) => {
              if (date) {
                onChange(format(date, "yyyy-MM-dd"));
              }
              setOpen(false);
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

const DEFAULT_KB_TEMPLATE = `# Wi-Fi Details

- **Network Name:** [Add Wi-Fi name here]
- **Password:** [Add Wi-Fi password here]

# Trash & Recycling Schedule

- **Trash Pickup:** [Add trash pickup days, e.g., Mondays]
- **Recycling:** [Add recycling pickup days]
- **Instructions:** [Add any special instructions, bin location, etc.]

# Move-In Document Link

[Add link to move-in checklist or lease document here]

# House Rules

- [Add house rule 1]
- [Add house rule 2]
- [Add house rule 3]
- No smoking inside the property
- Quiet hours: 10pm - 8am

# Parking

- [Add parking details, e.g., street parking, driveway, assigned spots]
- [Add any restrictions or permits required]

# Appliance Instructions

## Washer/Dryer
- [Add instructions for washer/dryer]

## HVAC/Thermostat
- [Add thermostat instructions and preferred temperature range]

## Other Appliances
- [Add instructions for other major appliances]

# Emergency Contacts

- **Property Manager:** [Name] - [Phone]
- **Maintenance Emergency:** [Phone]
- **Local Police (non-emergency):** [Phone]
- **Nearest Hospital:** [Name and address]

# Local Info

- **Nearest Grocery:** [Store name and distance]
- **Nearest Pharmacy:** [Store name and distance]
- **Public Transit:** [Nearest bus/train stop]
- **Restaurants:** [Nearby options]

# Notes

[Add any additional notes, special instructions, or important information here]
`;

// RoomDimensionCell — inline width × length inputs with live sq-ft calculation.
// Saves on blur. When dimensions are absent, shows a gentle proxy-category note
// so the host knows PadSplit's size_category is being used as an analytics proxy.
function RoomDimensionCell({
  room,
  readOnly,
  onSave,
}: {
  room: Room;
  readOnly: boolean;
  onSave: (data: { widthFt: string | null; lengthFt: string | null; squareFootage: string | null }) => void;
}) {
  const [width, setWidth] = useState<string>(room.widthFt != null ? String(room.widthFt) : "");
  const [length, setLength] = useState<string>(room.lengthFt != null ? String(room.lengthFt) : "");

  const wNum = parseFloat(width);
  const lNum = parseFloat(length);
  const sqFt = !isNaN(wNum) && wNum > 0 && !isNaN(lNum) && lNum > 0 ? (wNum * lNum).toFixed(1) : null;

  function handleBlur() {
    const w = width.trim();
    const l = length.trim();
    const wVal = w !== "" ? w : null;
    const lVal = l !== "" ? l : null;
    const sqVal =
      wVal != null && lVal != null && !isNaN(parseFloat(wVal)) && !isNaN(parseFloat(lVal))
        ? String(parseFloat(wVal) * parseFloat(lVal))
        : null;
    const prevW = room.widthFt != null ? String(room.widthFt) : null;
    const prevL = room.lengthFt != null ? String(room.lengthFt) : null;
    if (wVal !== prevW || lVal !== prevL) {
      onSave({ widthFt: wVal, lengthFt: lVal, squareFootage: sqVal });
    }
  }

  return (
    <div className="flex flex-col gap-1 w-[104px]" data-testid={`cell-dimensions-${room.id}`}>
      <div className="flex items-center gap-1">
        <Input
          value={width}
          onChange={(e) => setWidth(e.target.value)}
          onBlur={handleBlur}
          placeholder="W ft"
          className="text-xs h-7 w-12 px-1"
          disabled={readOnly}
          data-testid={`input-room-width-${room.id}`}
        />
        <span className="text-xs text-gray-400">×</span>
        <Input
          value={length}
          onChange={(e) => setLength(e.target.value)}
          onBlur={handleBlur}
          placeholder="L ft"
          className="text-xs h-7 w-12 px-1"
          disabled={readOnly}
          data-testid={`input-room-length-${room.id}`}
        />
      </div>
      {sqFt != null ? (
        <span className="text-xs text-muted-foreground" data-testid={`text-sqft-${room.id}`}>
          {sqFt} sq ft
        </span>
      ) : room.sizeCategory ? (
        <span className="text-[10px] text-muted-foreground italic leading-tight" data-testid={`text-size-proxy-${room.id}`}>
          Using PadSplit size ({room.sizeCategory}) as proxy
        </span>
      ) : null}
    </div>
  );
}

export default function AppPropertyDetail() {
  const queryClient = useQueryClient();
  const [, params] = useRoute("/app/properties/:id");
  const [pathname, navigate] = useLocation();
  const search = useSearch();
  const searchParams = new URLSearchParams(search);
  const checklistQueryOpen = searchParams.get("checklist") === "open";
  const destinationVisible = useHomeCardVisibility();
  const { canEdit, canView } = usePermissions();
  const promptSettingsVisible = destinationVisible("/app/prompt-library") && canView("image_generator");
  const [setupChecklistExpanded, setSetupChecklistExpanded] = useState(checklistQueryOpen);
  const [pendingSetupAnchor, setPendingSetupAnchor] = useState<string | null>(null);
  const setupAnchorPulseTimer = useRef<number | null>(null);
  const setupAnchorPulseTarget = useRef<HTMLElement | null>(null);
  useEffect(() => setSetupChecklistExpanded(checklistQueryOpen), [params?.id, checklistQueryOpen]);
  const docFilterActive =
    searchParams.has("kind") ||
    searchParams.has("vendor") ||
    searchParams.has("sort") ||
    searchParams.has("dir") ||
    searchParams.has("q") ||
    searchParams.has("min") ||
    searchParams.has("max") ||
    searchParams.has("page") ||
    searchParams.has("pageSize");
  const effectiveDefaultPropertyTab = docFilterActive ? "documents" : "overview";
  const [activePropertyTab, setActivePropertyTab] = useTabQueryParam("tab", effectiveDefaultPropertyTab, {
    tabs: [
      "overview",
      "rooms",
      "knowledge-base",
      "documents",
      "devices",
      "appliances",
      "maintenance",
      ...(promptSettingsVisible ? ["prompts-settings"] : []),
    ],
  });
  const VALID_DOC_SORT_FIELDS = ["uploadedAt", "vendor", "amount"] as const;
  const VALID_DOC_SORT_DIRS = ["asc", "desc"] as const;
  const VALID_DOC_KINDS = new Set<string>(["all", ...Object.keys(DOCUMENT_CATEGORY_LABELS)]);
  const rawKind = searchParams.get("kind");
  const docKindFilter = rawKind && VALID_DOC_KINDS.has(rawKind) ? rawKind : "all";
  const docVendorFilter = searchParams.get("vendor") || "all";
  const docNameQuery = searchParams.get("q") || "";
  const rawMin = searchParams.get("min");
  const rawMax = searchParams.get("max");
  const docMinAmount = rawMin && !isNaN(parseFloat(rawMin)) ? rawMin : "";
  const docMaxAmount = rawMax && !isNaN(parseFloat(rawMax)) ? rawMax : "";
  const rawSort = searchParams.get("sort");
  const docSortField = (VALID_DOC_SORT_FIELDS as readonly string[]).includes(rawSort || "")
    ? (rawSort as "uploadedAt" | "vendor" | "amount")
    : "uploadedAt";
  const rawDir = searchParams.get("dir");
  const docSortDir = (VALID_DOC_SORT_DIRS as readonly string[]).includes(rawDir || "")
    ? (rawDir as "asc" | "desc")
    : "desc";

  const updateDocsQuery = (updates: Record<string, string | null>, options?: { keepPage?: boolean }) => {
    const next = new URLSearchParams(search);
    for (const [key, value] of Object.entries(updates)) {
      if (value === null || value === "") next.delete(key);
      else next.set(key, value);
    }
    if (!options?.keepPage) next.delete("page");
    const qs = next.toString();
    navigate(qs ? `${pathname}?${qs}` : pathname, { replace: true });
  };

  const updateChecklistExpanded = (expanded: boolean) => {
    setSetupChecklistExpanded(expanded);
    const next = new URLSearchParams(search);
    if (expanded) next.set("checklist", "open");
    else next.delete("checklist");
    const qs = next.toString();
    navigate(qs ? `${pathname}?${qs}` : pathname, { replace: true });
  };

  const handleChecklistNavigate = (href: string, event: MouseEvent<HTMLAnchorElement>) => {
    if (!href.startsWith("#")) return;
    event.preventDefault();
    setPendingSetupAnchor(href.slice(1));
    if (activePropertyTab !== "overview") setActivePropertyTab("overview");
  };

  useEffect(() => {
    if (!pendingSetupAnchor || activePropertyTab !== "overview") return;
    const target = document.getElementById(pendingSetupAnchor);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
      setupAnchorPulseTarget.current?.classList.remove("setup-target-pulse");
      if (setupAnchorPulseTimer.current !== null) window.clearTimeout(setupAnchorPulseTimer.current);
      target.classList.remove("setup-target-pulse");
      void target.offsetWidth;
      target.classList.add("setup-target-pulse");
      setupAnchorPulseTarget.current = target;
      setupAnchorPulseTimer.current = window.setTimeout(() => {
        target.classList.remove("setup-target-pulse");
        setupAnchorPulseTarget.current = null;
        setupAnchorPulseTimer.current = null;
      }, 2200);
    }
    setPendingSetupAnchor(null);
  }, [activePropertyTab, pendingSetupAnchor]);

  useEffect(() => () => {
    if (setupAnchorPulseTimer.current !== null) window.clearTimeout(setupAnchorPulseTimer.current);
    setupAnchorPulseTarget.current?.classList.remove("setup-target-pulse");
  }, []);

  const handleDocSort = (field: "uploadedAt" | "vendor" | "amount") => {
    if (docSortField === field) {
      updateDocsQuery({ sort: field, dir: docSortDir === "asc" ? "desc" : "asc" });
    } else {
      const defaultDir = field === "uploadedAt" || field === "amount" ? "desc" : "asc";
      updateDocsQuery({ sort: field, dir: defaultDir });
    }
  };

  const { toast } = useToast();
  const readOnly = !canEdit("properties");
  const checklistDevicesAllowed = destinationVisible("/app/devices/smart-locks") && destinationVisible("/app/devices/thermostats");
  const propertyId = params?.id ? parseInt(params.id) : 0;

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam && PROPERTY_REDIRECT_TAB_TARGETS[tabParam] && propertyId) {
      navigate(`${PROPERTY_REDIRECT_TAB_TARGETS[tabParam]}?propertyId=${propertyId}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, propertyId]);

  useEffect(() => {
    if (!propertyId) return;
    try {
      window.localStorage.setItem("padpulse:lastSelectedPropertyId", String(propertyId));
    } catch {
      // ignore localStorage errors
    }
  }, [propertyId]);
  const [addRoomOpen, setAddRoomOpen] = useState(false);
  const [newRoomNumber, setNewRoomNumber] = useState("");
  const [newRoomType, setNewRoomType] = useState("bedroom");
  const [expandedRoomTitles, setExpandedRoomTitles] = useState<Set<number>>(new Set());
  const [roomsView, setRoomsView] = useState<"data" | "images">(
    () => (searchParams.get("view") === "images" ? "images" : "data"),
  );
  const [roomCategory, setRoomCategory] = useState<"all" | "bedroom" | "bathroom" | "common_space" | "exterior">("all");
  const [variantsRoomId, setVariantsRoomId] = useState<number | null>(null);
  const variantsTriggerRef = useRef<HTMLButtonElement | null>(null);
  const previewTriggerRef = useRef<HTMLElement | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [failedImageUrls, setFailedImageUrls] = useState<Set<string>>(new Set());
  const markImageFailed = (url: string) =>
    setFailedImageUrls((prev) => (prev.has(url) ? prev : new Set(prev).add(url)));
  const [propertyStatus, setPropertyStatus] = useState<string>("");
  const [statusError, setStatusError] = useState<string | null>(null);
  // Task #2900 — Delete property confirmation dialog. The host must type
  // "delete" (case-insensitive) before the confirm button activates.
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  // Task #1916 — Archive confirmation modal state. When the host picks
  // "Archive" we first fetch the linked Mercury cards; if any exist we open
  // this modal so the host can choose Cancel-in-Mercury vs. Unassign-only.
  const [archiveCardModalOpen, setArchiveCardModalOpen] = useState(false);
  const [archiveLinkedCards, setArchiveLinkedCards] = useState<
    { id: number; nickname: string | null; last4: string | null; brand: string | null; mercuryCardId: string | null }[]
  >([]);
  const [archivePending, setArchivePending] = useState(false);
  // Task #2872 — which terminal status (archive/sold/disposed) the host is
  // moving into, captured when the linked-card modal opens so the chosen
  // mercuryCardAction is applied alongside the right status.
  const [pendingTerminalStatus, setPendingTerminalStatus] = useState<string | null>(null);
  // Task #3824 — When the cancel prompt is re-opened from a notification /
  // 30-day reminder (?mercuryPrompt=1) the property is already terminal, so the
  // host's choice is applied via the decision endpoint, not a status change.
  const [mercuryRepromptMode, setMercuryRepromptMode] = useState(false);
  const [mercuryPromptHandled, setMercuryPromptHandled] = useState(false);
  // Sale/disposition details modal. Opened when the host moves a property to
  // "Sold" or "Disposed" so they can optionally record a sale date + amount.
  const [saleModalOpen, setSaleModalOpen] = useState(false);
  const [pendingSaleStatus, setPendingSaleStatus] = useState<"sold" | "disposed" | null>(null);
  const [saleDateInput, setSaleDateInput] = useState("");
  const [salePriceInput, setSalePriceInput] = useState("");
  // Carries any extra fields (e.g. sale date/price) collected before the
  // terminal-status Mercury-card modal, so they're applied with the final
  // status mutation once the host picks a Mercury card action.
  const [pendingTerminalPatch, setPendingTerminalPatch] = useState<Partial<Property>>({});
  const [kbEditMode, setKbEditMode] = useState(false);
  const [kbDraft, setKbDraft] = useState<string | null>(null);
  const [createUnitOpen, setCreateUnitOpen] = useState(false);
  const [createUnitName, setCreateUnitName] = useState("");
  const [createUnitAddress, setCreateUnitAddress] = useState("");
  const [createUnitRooms, setCreateUnitRooms] = useState("0");
  const [createUnitBaths, setCreateUnitBaths] = useState("0");
  const [docToDelete, setDocToDelete] = useState<{ id: number; fileName: string } | null>(null);
  const [linkShadowOpen, setLinkShadowOpen] = useState(false);
  const [linkShadowRoomId, setLinkShadowRoomId] = useState<number | null>(null);
  const [linkShadowTargetId, setLinkShadowTargetId] = useState<string>("");
  const propertyPhotoInputRef = useRef<HTMLInputElement | null>(null);
  const propertyPhotoUploadLock = useRef(false);
  const roomImageUploadLocks = useRef(new Set<number>());
  const [roomImageUploadingIds, setRoomImageUploadingIds] = useState<Set<number>>(new Set());
  const [propertyPhotoUploading, setPropertyPhotoUploading] = useState(false);
  const [propertyPhotoLoadFailed, setPropertyPhotoLoadFailed] = useState(false);
  const [videoUrlError, setVideoUrlError] = useState<string | null>(null);

  // Task #2066 — Appliances tab
  const [applianceFormOpen, setApplianceFormOpen] = useState(false);
  const [applianceToEdit, setApplianceToEdit] = useState<import("@shared/schema").Appliance | null>(null);
  const [applianceToDelete, setApplianceToDelete] = useState<import("@shared/schema").Appliance | null>(null);
  const [applianceDetailId, setApplianceDetailId] = useState<number | null>(null);

  const propertyQuery = useQuery<Property>({
    queryKey: ["/api/app/properties", propertyId],
    queryFn: async () => {
      const res = await fetch(`/api/app/properties/${propertyId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Not found");
      return res.json();
    },
    enabled: !!propertyId,
  });
  usePageTitle(propertyQuery.data?.address || "Property");

  const propertyPhotoQuery = useQuery<{ imageUrl: string | null }>({
    queryKey: ["/api/app/properties", propertyId, "photo"],
    queryFn: async () => {
      const res = await fetch(`/api/app/properties/${propertyId}/photo`, { credentials: "include" });
      if (!res.ok) throw responseError(res.status, "Couldn't load property photo");
      return res.json();
    },
    enabled: !!propertyId,
  });
  useEffect(() => setPropertyPhotoLoadFailed(false), [propertyId, propertyPhotoQuery.data?.imageUrl]);

  const allPropertiesQuery = useQuery<Property[]>({
    queryKey: ["/api/app/properties"],
  });

  // Task #4650 — owners of this property (id + display name) for the
  // "PadSplit message sender" selector.
  const propertyOwnersQuery = useQuery<{ id: number; name: string }[]>({
    queryKey: ["/api/app/properties", propertyId, "owners"],
    enabled: !!propertyId,
  });

  const thirdPartyListingsQuery = useQuery<ThirdPartyListing[]>({
    queryKey: ["/api/app/marketing/third-party-listings"],
  });

  const marketingSettingsQuery = useQuery<MarketingSettings | null>({
    queryKey: ["/api/app/marketing/settings"],
  });

  const activeThirdPartyListings = (thirdPartyListingsQuery.data ?? []).filter(
    (l) => l.propertyId === propertyId && l.status === "active"
  );
  const activeThirdPartyListingCount = activeThirdPartyListings.length;

  const DEFAULT_LISTING_PLATFORMS = [
    { id: "zillow", name: "Zillow" },
    { id: "roomies", name: "Roomies" },
    { id: "spare_room", name: "Spare Room" },
    { id: "hotpads", name: "HotPads" },
    { id: "facebook_marketplace", name: "Facebook Marketplace" },
    { id: "craigslist", name: "Craigslist" },
    { id: "furnish_finders", name: "Furnish Finders" },
  ];
  const customListingSites: CustomListingSite[] = Array.isArray(marketingSettingsQuery.data?.customListingSites)
    ? (marketingSettingsQuery.data!.customListingSites as CustomListingSite[])
    : [];
  function getPlatformName(id: string): string {
    const builtIn = DEFAULT_LISTING_PLATFORMS.find((p) => p.id === id);
    if (builtIn) return builtIn.name;
    const custom = customListingSites.find((s) => s.id === id);
    if (custom) return custom.name;
    return id;
  }
  const platformBreakdown = (() => {
    const counts = new Map<string, number>();
    for (const l of activeThirdPartyListings) {
      counts.set(l.platform, (counts.get(l.platform) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([id, count]) => ({ id, name: getPlatformName(id), count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  })();

  const roomsQuery = useQuery<PropertyRoom[]>({
    // Task #2112: the room editor needs to see shadow rows so it can render
    // the shadow-management table; everywhere else the server now strips
    // shadow rooms by default. The extra "all" key segment keeps this
    // request from sharing a cache slot with PropertyDevicesPanel's
    // primary-only fetch on the same page.
    queryKey: ["/api/app/properties", propertyId, "rooms", "all"],
    queryFn: async () => {
      const res = await fetch(`/api/app/properties/${propertyId}/rooms?includeAll=true&includeImageSummary=true`, { credentials: "include" });
      if (!res.ok) throw responseError(res.status, "Failed to load property rooms");
      return res.json();
    },
    enabled: !!propertyId,
    refetchInterval: (query) => {
      const data = query.state.data as (Room & { imageStatus?: string })[] | undefined;
      const hasProcessing = Array.isArray(data) && data.some((r) => r.imageStatus === "processing");
      return hasProcessing ? 4000 : false;
    },
  });

  const imagePromptsQuery = useQuery<ImagePrompt[]>({
    queryKey: ["/api/app/image-prompts"],
  });


  const propertyMembersQuery = useQuery<{ id: number; name: string; displayName: string | null; roomId: number | null; status?: string | null; residency?: import("@shared/broadcast-recipient-review").MemberResidencyView | null }[]>({
    queryKey: ["/api/app/properties", propertyId, "members"],
    queryFn: async () => {
      const res = await fetch(`/api/app/properties/${propertyId}/members`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    enabled: !!propertyId,
  });

  const profileQuery = useQuery<{ excludeInactiveRoomsFromOccupancy: boolean }>({
    queryKey: ["/api/app/profile"],
  });

  const aiDraftingAccessQuery = useQuery<{ allowed: boolean }>({
    queryKey: ["/api/app/ai-drafting/access"],
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  // Fail CLOSED: hidden by default unless the server confirms access.
  const aiDraftingAllowed = aiDraftingAccessQuery.data?.allowed === true;

  const updateMutation = useMutation({
    mutationFn: async (data: Partial<Property> & { mercuryCardAction?: "cancel" | "not_now" | "remind_30d" }) => {
      if (readOnly) throw new Error("You have view-only access to this property.");
      const res = await apiRequest("PATCH", `/api/app/properties/${propertyId}`, data);
      // Task #1940 — Pull the response body so we can surface the server's
      // mercuryCancelSkipped warning when "Cancel in Mercury" was a no-op.
      try {
        return await res.json();
      } catch {
        return null;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties"] });
      // Task #1940 — If the host clicked "Cancel in Mercury" but the server
      // couldn't actually cancel anything (no Mercury connection / inactive
      // / missing API key), warn them with both a toast AND the inline
      // status-error line so they don't assume the cards were cancelled.
      const warnings = (data && typeof data === "object" && "warnings" in (data as Record<string, unknown>))
        ? (data as { warnings?: { mercuryCancelSkipped?: { message?: string }; padsplitUnlinked?: { assistantName?: string | null } } }).warnings
        : undefined;
      const mercurySkipped = warnings?.mercuryCancelSkipped;
      const padsplitUnlinked = warnings?.padsplitUnlinked;
      if (mercurySkipped && mercurySkipped.message) {
        setStatusError(mercurySkipped.message);
        toast({
          title: "Mercury cards weren't cancelled",
          description: mercurySkipped.message,
          variant: "destructive",
        });
      } else {
        toast({ title: "Property updated" });
      }
      if (padsplitUnlinked) {
        const assistantName = padsplitUnlinked.assistantName;
        const desc = assistantName
          ? `Invite ${assistantName} (${assistantName}@appmail.padpulse.io) as a team member in PadSplit so data can sync.`
          : "Invite your PadPulse assistant as a team member in PadSplit to enable syncing.";
        toast({
          title: "Link this property to PadSplit",
          description: desc,
          action: (
            <Link
              href="/app/padsplit-sync"
              className="inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium transition-colors hover:bg-secondary focus:outline-none focus:ring-1 focus:ring-ring"
              data-testid="toast-action-padsplit-sync"
            >
              Go to Sync
            </Link>
          ) as React.ReactElement,
        });
      }
    },
    onError: (error: Error) => {
      if (error.message.includes("401")) {
        toast({ title: "Session expired", description: "Please log in again.", variant: "destructive" });
        navigate("/app/login");
      } else {
        const msg = parseErrorMessage(error, "Couldn't update this property. Please try again.");
        const isActiveMembersBlock = msg.includes("members assigned") || (() => {
          try {
            const match = error.message.match(/^(\d{3}):\s*([\s\S]*)$/);
            const parsed = JSON.parse(match ? match[2] : error.message);
            return parsed?.code === "ACTIVE_MEMBERS_PRESENT";
          } catch { return false; }
        })();
        if (isActiveMembersBlock) {
          setPropertyStatus(property?.status ?? "active");
          setStatusError("Cannot change status while active members are assigned to rooms. Remove or reassign all members first.");
        } else {
          setStatusError(null);
          toast({ title: "Failed to update property", description: msg || undefined, variant: "destructive" });
        }
      }
    },
  });

  // Task #3824 — Re-opened cancel prompt (property is already terminal, so the
  // host's choice is applied via the dedicated decision endpoint instead of a
  // status PATCH). Surfaces the same mercuryCancelSkipped warning as the PATCH
  // path when "Yes, cancel cards" couldn't reach a usable Mercury connection.
  const mercuryDecisionMutation = useMutation({
    mutationFn: async (action: "cancel" | "not_now" | "remind_30d") => {
      const res = await apiRequest("POST", `/api/app/properties/${propertyId}/mercury-cancel-decision`, { action });
      try {
        return await res.json();
      } catch {
        return null;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties"] });
      const mercurySkipped = (data && typeof data === "object" && "mercuryCancelSkipped" in (data as Record<string, unknown>))
        ? (data as { mercuryCancelSkipped?: { message?: string } | null }).mercuryCancelSkipped
        : undefined;
      if (mercurySkipped && mercurySkipped.message) {
        setStatusError(mercurySkipped.message);
        toast({
          title: "Mercury cards weren't cancelled",
          description: mercurySkipped.message,
          variant: "destructive",
        });
      } else {
        toast({ title: "Property updated" });
      }
    },
    onError: (error: Error) => {
      if (error.message.includes("401")) {
        toast({ title: "Session expired", description: "Please log in again.", variant: "destructive" });
        navigate("/app/login");
        return;
      }
      const msg = parseErrorMessage(error, "Couldn't update the Mercury card prompt. Please try again.");
      toast({ title: "Failed to update property", description: msg || undefined, variant: "destructive" });
    },
  });

  // Task #2900 — Permanently delete this property. The route + guards
  // (active-member / Mercury card checks) already live on the backend.
  const deletePropertyMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/app/properties/${propertyId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties"] });
      setDeleteDialogOpen(false);
      setDeleteConfirmText("");
      toast({ title: "Property deleted" });
      navigate("/app/properties");
    },
    onError: (error: Error) => {
      setDeleteDialogOpen(false);
      setDeleteConfirmText("");
      toast({ title: "Failed to delete property", description: parseErrorMessage(error, "Couldn't delete this property. Please try again."), variant: "destructive" });
    },
  });

  const createUnitMutation = useMutation({
    mutationFn: async (property: Property) => {
      if (readOnly) throw new Error("You have view-only access to this property.");
      const parentId = property.parentPropertyId ?? property.id;
      const trimmedAddress = createUnitAddress.trim();
      const payload = {
        name: createUnitName.trim(),
        ...(trimmedAddress ? { address: trimmedAddress } : {}),
        roomCount: Math.max(0, parseInt(createUnitRooms) || 0),
        bathroomCount: Math.max(0, parseInt(createUnitBaths) || 0),
        parentPropertyId: parentId,
        status: "active",
      };
      return apiRequest("POST", "/api/app/properties", payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId] });
      setCreateUnitOpen(false);
      toast({ title: "Unit created", description: "The new unit has been added." });
    },
    onError: () => {
      toast({ title: "Failed to create unit", variant: "destructive" });
    },
  });

  const addRoomMutation = useMutation({
    mutationFn: async ({ roomNumber, roomType }: { roomNumber: string; roomType: string }) => {
      if (readOnly) throw new Error("You have view-only access to this property.");
      await apiRequest("POST", `/api/app/properties/${propertyId}/rooms`, { roomNumber, roomType });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "rooms"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties"] });
      setAddRoomOpen(false);
      setNewRoomNumber("");
      setNewRoomType("bedroom");
      toast({ title: "Room added" });
    },
  });

  const deleteRoomMutation = useMutation({
    mutationFn: async (roomId: number) => {
      if (readOnly) throw new Error("You have view-only access to this property.");
      await apiRequest("DELETE", `/api/app/rooms/${roomId}`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "rooms"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties"] });
      toast({ title: "Room removed" });
    },
  });

  const updateRoomMutation = useMutation({
    mutationFn: async ({ roomId, data }: { roomId: number; data: Partial<Room> }) => {
      if (readOnly) throw new Error("You have view-only access to this property.");
      const res = await apiRequest("PATCH", `/api/app/rooms/${roomId}`, data);
      return res.json() as Promise<Room>;
    },
    onSuccess: (updatedRoom, variables) => {
      queryClient.setQueriesData<Room[]>({ queryKey: ["/api/app/properties", propertyId, "rooms"] }, old =>
        old?.map(room => room.id === variables.roomId ? { ...room, ...updatedRoom } : room));
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "rooms"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties"] });
      if ("inputImageUrl" in variables.data) {
        toast({ title: variables.data.inputImageUrl === null ? "Input image removed" : "Image uploaded" });
      }
    },
    onError: (error: Error) => {
      toast({ title: "Failed to update room", description: parseErrorMessage(error, "Couldn't update this room. Please try again."), variant: "destructive" });
    },
  });

  const knowledgeBaseQuery = useQuery<{ content: string | null }>({
    queryKey: ["/api/app/properties", propertyId, "knowledge-base"],
    queryFn: async () => {
      const res = await fetch(`/api/app/properties/${propertyId}/knowledge-base`, { credentials: "include" });
      if (!res.ok) throw responseError(res.status, "Failed to load Knowledge Base");
      return res.json();
    },
    enabled: !!propertyId,
  });

  const saveKbMutation = useMutation({
    mutationFn: async (content: string) => {
      if (readOnly) return null;
      const res = await fetch(`/api/app/properties/${propertyId}/knowledge-base`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content }),
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to save");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "knowledge-base"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties"] });
      setKbEditMode(false);
      setKbDraft(null);
      toast({ title: "Knowledge base saved" });
    },
    onError: () => {
      toast({ title: "Failed to save knowledge base", variant: "destructive" });
    },
  });

  const meQuery = useQuery<{ user: { id: number; selectedPlan?: string }; effectivePlan?: AccountType }>({ queryKey: ["/api/app/auth/me"] });
  const currentUserId = meQuery.data?.user?.id;
  const [manageDocId, setManageDocId] = useState<number | null>(null);

  const rawDocPage = parseInt(searchParams.get("page") || "1");
  const docPage = Number.isFinite(rawDocPage) && rawDocPage >= 1 ? rawDocPage : 1;
  const DOC_PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
  const DOC_PAGE_SIZE_STORAGE_KEY = "documents:pageSize";
  const isValidDocPageSize = (n: number): boolean =>
    (DOC_PAGE_SIZE_OPTIONS as readonly number[]).includes(n);
  const readStoredDocPageSize = (): number => {
    if (typeof window === "undefined") return 50;
    try {
      const stored = parseInt(window.localStorage.getItem(DOC_PAGE_SIZE_STORAGE_KEY) || "");
      return isValidDocPageSize(stored) ? stored : 50;
    } catch {
      return 50;
    }
  };
  const urlPageSizeParam = searchParams.get("pageSize");
  const rawDocPageSize = urlPageSizeParam !== null
    ? parseInt(urlPageSizeParam)
    : readStoredDocPageSize();
  const docPageSize = isValidDocPageSize(rawDocPageSize) ? rawDocPageSize : 50;

  const persistDocPageSize = (size: number) => {
    if (!isValidDocPageSize(size)) return;
    try {
      window.localStorage.setItem(DOC_PAGE_SIZE_STORAGE_KEY, String(size));
    } catch {
      // ignore storage errors (e.g., private mode, quota exceeded)
    }
  };

  const documentsQuery = useQuery<{
    items: PropertyDocumentRow[];
    total: number;
    page: number;
    pageSize: number;
    vendorOptions: string[];
  }>({
    queryKey: [
      "/api/app/properties",
      propertyId,
      "documents",
      { kind: docKindFilter, vendor: docVendorFilter, sort: docSortField, dir: docSortDir, page: docPage, pageSize: docPageSize, q: docNameQuery, min: docMinAmount, max: docMaxAmount },
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (docKindFilter !== "all") params.set("kind", docKindFilter);
      if (docVendorFilter !== "all") params.set("vendor", docVendorFilter);
      if (docNameQuery.trim() !== "") params.set("q", docNameQuery.trim());
      if (docMinAmount !== "") params.set("min", docMinAmount);
      if (docMaxAmount !== "") params.set("max", docMaxAmount);
      params.set("sort", docSortField);
      params.set("dir", docSortDir);
      params.set("page", String(docPage));
      params.set("pageSize", String(docPageSize));
      const res = await fetch(`/api/app/properties/${propertyId}/documents?${params.toString()}`, { credentials: "include" });
      if (!res.ok) throw responseError(res.status, "Failed to load documents");
      return res.json();
    },
    enabled: !!propertyId,
  });

  const checklistDocumentsQuery = useQuery<{ total: number }>({
    queryKey: ["/api/app/properties", propertyId, "documents", "setup-checklist"],
    queryFn: async () => {
      const res = await fetch(`/api/app/properties/${propertyId}/documents?page=1&pageSize=1`, { credentials: "include" });
      if (!res.ok) throw responseError(res.status, "Failed to load supporting documents");
      return res.json();
    },
    enabled: !!propertyId,
  });

  useEffect(() => {
    const data = documentsQuery.data;
    if (!data) return;
    const totalPages = Math.max(1, Math.ceil(data.total / docPageSize));
    if (docPage > totalPages) {
      const next = new URLSearchParams(search);
      if (totalPages <= 1) next.delete("page");
      else next.set("page", String(totalPages));
      const qs = next.toString();
      navigate(qs ? `${pathname}?${qs}` : pathname, { replace: true });
    }
  }, [documentsQuery.data, docPage, docPageSize, search, pathname, navigate]);

  // Task #2066 — Appliances
  const appliancesQuery = useQuery<ApplianceWithTickets[]>({
    queryKey: ["/api/app/properties", propertyId, "appliances"],
    queryFn: async () => {
      const res = await fetch(`/api/app/properties/${propertyId}/appliances`, { credentials: "include" });
      if (!res.ok) throw responseError(res.status, "Failed to load appliances");
      return res.json();
    },
    enabled: !!propertyId,
  });

  const checklistDevicesQuery = useQuery<{
    thermostats: unknown[];
    locks: unknown[];
    assignableLocks?: unknown[];
    ignoredLockCount?: number;
  }>({
    queryKey: ["/api/app/properties", propertyId, "devices", "configuration"],
    queryFn: async () => {
      const res = await fetch(`/api/app/properties/${propertyId}/devices?configurationOnly=true`, { credentials: "include" });
      if (!res.ok) throw responseError(res.status, "Failed to load linked devices");
      return res.json();
    },
    enabled: !!propertyId && checklistDevicesAllowed,
  });

  const createApplianceMutation = useMutation({
    mutationFn: async (data: Partial<import("@shared/schema").InsertAppliance>) => {
      if (readOnly) throw new Error("You have view-only access to this property.");
      const res = await apiRequest("POST", `/api/app/properties/${propertyId}/appliances`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "appliances"] });
      setApplianceFormOpen(false);
      setApplianceToEdit(null);
      toast({ title: "Appliance added" });
    },
    onError: (err: Error) => {
      toast({ title: "Failed to add appliance", description: parseErrorMessage(err, "Couldn't add this appliance. Please try again."), variant: "destructive" });
    },
  });

  const updateApplianceMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<import("@shared/schema").InsertAppliance> }) => {
      if (readOnly) throw new Error("You have view-only access to this property.");
      const res = await apiRequest("PATCH", `/api/app/properties/${propertyId}/appliances/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "appliances"] });
      setApplianceFormOpen(false);
      setApplianceToEdit(null);
      toast({ title: "Appliance updated" });
    },
    onError: (err: Error) => {
      toast({ title: "Failed to update appliance", description: parseErrorMessage(err, "Couldn't update this appliance. Please try again."), variant: "destructive" });
    },
  });

  const deleteApplianceMutation = useMutation({
    mutationFn: async (id: number) => {
      if (readOnly) throw new Error("You have view-only access to this property.");
      await apiRequest("DELETE", `/api/app/properties/${propertyId}/appliances/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "appliances"] });
      setApplianceToDelete(null);
      toast({ title: "Appliance deleted" });
    },
    onError: (err: Error) => {
      toast({ title: "Failed to delete appliance", description: parseErrorMessage(err, "Couldn't delete this appliance. Please try again."), variant: "destructive" });
    },
  });

  const deleteDocumentMutation = useMutation({
    mutationFn: async (id: number) => {
      if (readOnly) throw new Error("You have view-only access to this property.");
      await apiRequest("DELETE", `/api/app/property-documents/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "documents"] });
      toast({ title: "Document deleted" });
      const newSearch = new URLSearchParams(search);
      if (newSearch.has("page")) {
        newSearch.delete("page");
        const qs = newSearch.toString();
        navigate(qs ? `${pathname}?${qs}` : pathname, { replace: true });
      }
    },
    onError: (error: Error) => {
      toast({ title: "Failed to delete document", description: parseErrorMessage(error, "Couldn't delete this document. Please try again."), variant: "destructive" });
    },
  });

  const generateRoomImageMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async ({ roomId, promptId }: { roomId: number; promptId?: number }) => {
      await apiRequest("POST", `/api/app/rooms/${roomId}/generate`, promptId ? { promptId } : {});
    },
    onSuccess: () => {
      toast({ title: "Generating", description: "Please wait a moment." });
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "rooms"] });
      }, 4000);
    },
    onError: (error: Error) => {
      toast({ title: "Generation failed", description: parseErrorMessage(error, "Couldn't generate the room image. Please try again."), variant: "destructive" });
    },
  });

  const createVariantMutation = useMutation({
    mutationFn: async (roomId: number) => {
      await apiRequest("POST", `/api/app/rooms/${roomId}/variants`, {});
    },
    onSuccess: (_, roomId) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/rooms", roomId, "variants"] });
      toast({ title: "Saved as variant" });
    },
  });


  const handleFieldBlur = (field: keyof Property, value: string) => {
    const property = propertyQuery.data;
    if (!property) return;
    if (String(property[field] ?? "") !== value) {
      const numericFields = ["roomCount", "bathroomCount"];
      const parsed = numericFields.includes(field) ? parseInt(value) || 0 : value;
      updateMutation.mutate({ [field]: parsed } as Partial<Property>);
    }
  };

  // Task #1916 / #2872 — Moving a property into a terminal status (Archive,
  // Sold, or Disposed) triggers a confirmation modal when the property has
  // linked Mercury cards so the host explicitly chooses cancel-vs-skip.
  // For any other status (or when no Mercury cards are linked) we apply
  // the change immediately. Going terminal→terminal (e.g. Archive→Sold)
  // skips the check since the cards were already handled. `extraPatch` carries
  // any fields collected earlier (e.g. sale date/price) so they're applied
  // alongside the status — whether or not the Mercury modal intervenes.
  const applyStatusChangeWithMercuryCheck = async (
    value: string,
    extraPatch: Partial<Property> = {},
  ) => {
    const TERMINAL_STATUSES = ["archive", "sold", "disposed"];
    const enteringTerminalStatus =
      TERMINAL_STATUSES.includes(value) && !TERMINAL_STATUSES.includes(property?.status ?? "");
    if (enteringTerminalStatus) {
      setArchivePending(true);
      try {
        const res = await fetch(`/api/app/properties/${propertyId}/mercury-cards`, { credentials: "include" });
        const linked = res.ok ? await res.json() : [];
        if (Array.isArray(linked) && linked.length > 0) {
          setArchiveLinkedCards(linked);
          setPendingTerminalStatus(value);
          setPendingTerminalPatch(extraPatch);
          setArchiveCardModalOpen(true);
          setArchivePending(false);
          return;
        }
      } catch {
        // If the lookup fails we fall through without the modal — the server
        // never auto-cancels or auto-unassigns, so it just flags the prompt and
        // notifies the host to decide later, which is safe.
      }
      setArchivePending(false);
    }
    setPropertyStatus(value);
    updateMutation.mutate({ status: value, ...extraPatch } as Partial<Property>);
  };

  const handleStatusChange = async (value: string) => {
    // Task #2900 — "Delete" is a pseudo-option in the status dropdown. Don't
    // treat it as a status change; reset the select and open the confirm dialog.
    if (value === "__delete__") {
      setDeleteConfirmText("");
      setDeleteDialogOpen(true);
      return;
    }
    setStatusError(null);
    // When moving to Sold/Disposed, prompt for optional sale date + amount
    // first; submitSaleDetails then runs the Mercury-card check before applying.
    if ((value === "sold" || value === "disposed") && property?.status !== value) {
      setPendingSaleStatus(value);
      setSaleDateInput(property?.saleDate ?? "");
      setSalePriceInput(property?.salePrice ?? "");
      setSaleModalOpen(true);
      return;
    }
    await applyStatusChangeWithMercuryCheck(value);
  };

  const submitSaleDetails = () => {
    if (!pendingSaleStatus) return;
    const status = pendingSaleStatus;
    const extraPatch: Partial<Property> = {
      saleDate: saleDateInput.trim() ? saleDateInput.trim() : null,
      salePrice: salePriceInput.trim() ? salePriceInput.trim() : null,
    };
    setSaleModalOpen(false);
    setPendingSaleStatus(null);
    void applyStatusChangeWithMercuryCheck(status, extraPatch);
  };

  const submitArchiveWithMercuryAction = (action: "cancel" | "not_now" | "remind_30d") => {
    const status = pendingTerminalStatus ?? "archive";
    setArchiveCardModalOpen(false);
    if (mercuryRepromptMode) {
      // Re-opened from a notification / 30-day reminder: the property is already
      // terminal, so apply the decision via the dedicated endpoint (no status
      // change) rather than re-PATCHing the status.
      setMercuryRepromptMode(false);
      setPendingTerminalPatch({});
      mercuryDecisionMutation.mutate(action);
      return;
    }
    setPropertyStatus(status);
    updateMutation.mutate({ status, ...pendingTerminalPatch, mercuryCardAction: action } as Partial<Property> & { mercuryCardAction: "cancel" | "not_now" | "remind_30d" });
    setPendingTerminalPatch({});
  };

  // Task #3824 — Deep-link from the cancel-prompt notification
  // (/app/properties/:id?mercuryPrompt=1) re-opens the modal in "reprompt" mode
  // for a property that's already terminal. Strip the param first so a refresh /
  // back navigation doesn't reopen it, then load the still-linked cards.
  const property = propertyQuery.data;
  const displayAddress = useMemo(() => {
    if (!property) return "";
    const base = property.address || "";
    const zip = property.postalCode || "";
    if (!base) return "";
    if (!zip) return base;
    if (base.includes(zip)) return base;
    return `${base}, ${zip}`;
  }, [property?.address, property?.postalCode]);
  useEffect(() => {
    if (mercuryPromptHandled) return;
    if (new URLSearchParams(search).get("mercuryPrompt") !== "1") return;
    if (!propertyId || !property) return;
    setMercuryPromptHandled(true);
    const next = new URLSearchParams(search);
    next.delete("mercuryPrompt");
    const qs = next.toString();
    navigate(qs ? `${pathname}?${qs}` : pathname, { replace: true });
    const TERMINAL_STATUSES = ["archive", "sold", "disposed"];
    if (!TERMINAL_STATUSES.includes(property.status ?? "")) return;
    (async () => {
      try {
        const res = await fetch(`/api/app/properties/${propertyId}/mercury-cards`, { credentials: "include" });
        const linked = res.ok ? await res.json() : [];
        if (Array.isArray(linked) && linked.length > 0) {
          setArchiveLinkedCards(linked);
          setPendingTerminalStatus(property.status ?? "archive");
          setMercuryRepromptMode(true);
          setArchiveCardModalOpen(true);
        }
      } catch {
        // Lookup failed — leave the prompt as-is server-side; nothing to open.
      }
    })();
  }, [mercuryPromptHandled, search, propertyId, property, pathname, navigate]);

  const handleImageUpload = async (roomId: number, file: File) => {
    if (readOnly || roomImageUploadLocks.current.has(roomId)) return;
    if (!(PROPERTY_IMAGE_MIME_TYPES as readonly string[]).includes(file.type)) {
      toast({ title: "Unsupported image", description: "Choose a JPEG, PNG, WebP, or GIF image.", variant: "destructive" });
      return;
    }
    if (!file.size) {
      toast({ title: "Invalid image", description: "The selected file is empty.", variant: "destructive" });
      return;
    }
    if (file.size > PROPERTY_IMAGE_MAX_BYTES) {
      toast({ title: "Image is too large", description: "Room images must be no more than 10 MiB.", variant: "destructive" });
      return;
    }
    roomImageUploadLocks.current.add(roomId);
    setRoomImageUploadingIds((ids) => new Set(ids).add(roomId));
    try {
      let imageUrl: string;
      try {
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result;
            if (typeof result !== "string" || !result.includes(",")) {
              reject(new Error("The image file couldn't be read."));
              return;
            }
            resolve(result.slice(result.indexOf(",") + 1));
          };
          reader.onerror = () => reject(new Error("The image file couldn't be read."));
          reader.readAsDataURL(file);
        });
        const res = await fetch("/api/app/upload", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ filename: file.name, mimeType: file.type, data: base64 }),
          credentials: "include",
        });
        if (!res.ok) {
          const response = await res.json().catch(() => null) as { message?: string } | null;
          throw new Error(response?.message || `Image upload failed (${res.status}).`);
        }
        const response = await res.json() as { imageUrl?: string };
        if (!response.imageUrl) throw new Error("The upload returned no image URL.");
        imageUrl = response.imageUrl;
      } catch (error) {
        toast({
          title: "Couldn't upload room image",
          description: parseErrorMessage(error, "Please choose another image or try again."),
          variant: "destructive",
        });
        return;
      }
      // The mutation reports success only after the room record has been saved.
      // Its own onError toast includes the server's property/permission detail.
      await updateRoomMutation.mutateAsync({ roomId, data: { inputImageUrl: imageUrl } });
    } catch {
      // updateRoomMutation.onError already reported the association failure.
    } finally {
      roomImageUploadLocks.current.delete(roomId);
      setRoomImageUploadingIds((ids) => {
        const next = new Set(ids);
        next.delete(roomId);
        return next;
      });
    }
  };

  const handlePropertyPhotoSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = "";
    if (!file || readOnly) return;
    if (!(PROPERTY_IMAGE_MIME_TYPES as readonly string[]).includes(file.type)) {
      toast({ title: "Unsupported image", description: "Choose a JPEG, PNG, WebP, or GIF image.", variant: "destructive" });
      return;
    }
    if (file.size > PROPERTY_IMAGE_MAX_BYTES) {
      toast({ title: "Image is too large", description: "Property photos must be 10 MiB or smaller.", variant: "destructive" });
      return;
    }
    if (propertyPhotoUploadLock.current) return;
    propertyPhotoUploadLock.current = true;
    setPropertyPhotoUploading(true);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result;
          if (typeof result !== "string") return reject(new Error("Could not read image"));
          resolve(result.split(",")[1] ?? "");
        };
        reader.onerror = () => reject(new Error("Could not read image"));
        reader.readAsDataURL(file);
      });
      const uploadResponse = await fetch("/api/app/upload", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filename: file.name, mimeType: file.type, data: base64 }),
        credentials: "include",
      });
      if (!uploadResponse.ok) throw new Error("Image upload failed");
      const uploadResult = await uploadResponse.json() as { imageUrl?: string };
      if (!uploadResult.imageUrl) throw new Error("Image upload returned no image URL");
      const associationResponse = await fetch(`/api/app/properties/${propertyId}/photo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageUrl: uploadResult.imageUrl }),
        credentials: "include",
      });
      if (!associationResponse.ok) throw new Error("The image uploaded, but it could not be saved to this property");
      await associationResponse.json();
      await queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "photo"] });
      await queryClient.invalidateQueries({ queryKey: ["/api/app/properties"] });
      toast({ title: "Property photo saved" });
    } catch (error) {
      toast({
        title: "Couldn't save property photo",
        description: parseErrorMessage(error, "Please try again."),
        variant: "destructive",
      });
    } finally {
      propertyPhotoUploadLock.current = false;
      setPropertyPhotoUploading(false);
    }
  };

  if (propertyQuery.isLoading) {
    return (
      <AppLayout>
        <div className="p-6 space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-[400px] w-full" />
        </div>
      </AppLayout>
    );
  }

  if (!property) {
    return (
      <AppLayout>
        <div className="p-6 text-center py-20">
          <p className="text-gray-500">Property not found</p>
          <Button variant="ghost" asChild data-testid="link-back-properties">
            <Link href="/app/properties">Back to Properties</Link>
          </Button>
        </div>
      </AppLayout>
    );
  }

  const rooms = roomsQuery.data || [];
  const currentStatus = propertyStatus || property.status;

  const excludeInactive = profileQuery.data?.excludeInactiveRoomsFromOccupancy === true;
  const billableRooms = rooms.filter(
    (r) => r.roomType !== "bathroom" && !r.shadowOfRoomId,
  );
  const occupancyStats = computeOccupancy(billableRooms, excludeInactive);

  const orderedRooms = (() => {
    const parentRooms = rooms.filter(r => !r.shadowOfRoomId);
    const result: PropertyRoom[] = [];
    for (const parent of parentRooms) {
      result.push(parent);
      const shadows = rooms.filter(r => r.shadowOfRoomId === parent.id);
      result.push(...shadows);
    }
    const orphans = rooms.filter(r => r.shadowOfRoomId && !rooms.find(p => p.id === r.shadowOfRoomId));
    result.push(...orphans);
    return result;
  })();

  const filteredRooms = orderedRooms.filter(r => {
    if (roomCategory === "all") return true;
    if (roomCategory === "bedroom") return !r.roomType || r.roomType === "bedroom";
    if (roomCategory === "bathroom") return r.roomType === "bathroom";
    if (roomCategory === "common_space") return r.roomType === "common_space" || r.roomType === "common" || r.roomType === "common_area";
    if (roomCategory === "exterior") return r.roomType === "exterior";
    return true;
  });

  const memberByRoomId = new Map<number, { id: number; name: string; displayName: string | null; status?: string | null; residency?: import("@shared/broadcast-recipient-review").MemberResidencyView | null }>();
  for (const m of propertyMembersQuery.data ?? []) {
    if (m.roomId != null && m.residency?.eligible === true) memberByRoomId.set(m.roomId, m);
  }

  return (
    <AppLayout>
      <div className="p-6">
        <ViewOnlyBanner area="properties" />
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
          <Link href="/app/properties">
            <span className="hover:text-gray-700 cursor-pointer" data-testid="link-breadcrumb-properties">Properties</span>
          </Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-gray-900" data-testid="text-breadcrumb-current">{property.name}</span>
        </div>

        <div className="mb-4 min-w-0">
          <div className="flex min-w-0 flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex min-w-0 flex-wrap items-center gap-3">
                <h1 className="min-w-0 text-2xl font-bold text-gray-900" data-testid="text-property-title" data-tour="property-detail-heading">{property.name}</h1>
            {activeThirdPartyListingCount > 0 ? (
              <Popover>
                <PopoverTrigger asChild>
                  <span
                    role="button"
                    tabIndex={0}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 text-xs font-medium hover:bg-blue-100 dark:hover:bg-blue-900/50 cursor-pointer select-none"
                    data-testid="badge-third-party-listings-count"
                    title="See platform breakdown for this property"
                  >
                    <Megaphone className="h-3.5 w-3.5" />
                    <span data-testid="text-third-party-listings-count">{activeThirdPartyListingCount}</span>
                    <span>active third-party listing{activeThirdPartyListingCount === 1 ? "" : "s"}</span>
                  </span>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-72 p-2" data-testid="popover-third-party-platform-breakdown">
                  <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Active listings by platform
                  </div>
                  {platformBreakdown.length === 0 ? (
                    <div className="px-2 py-3 text-sm text-gray-500" data-testid="text-platform-breakdown-empty">
                      No active third-party listings yet.
                    </div>
                  ) : (
                    <div className="flex flex-col">
                      {platformBreakdown.map((p) => (
                        <Link
                          key={p.id}
                          href={`/app/marketing/third-party-listings?propertyId=${propertyId}&platform=${encodeURIComponent(p.id)}`}
                        >
                          <span
                            className="flex items-center justify-between gap-3 px-2 py-2 rounded-md text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                            data-testid={`link-platform-breakdown-${p.id}`}
                          >
                            <span className="truncate">{p.name}</span>
                            <span
                              className="inline-flex items-center justify-center min-w-[1.5rem] h-5 px-1.5 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold"
                              data-testid={`text-platform-breakdown-count-${p.id}`}
                            >
                              {p.count}
                            </span>
                          </span>
                        </Link>
                      ))}
                    </div>
                  )}
                  <div className="border-t mt-1 pt-1">
                    <Link href={`/app/marketing/third-party-listings?propertyId=${propertyId}`}>
                      <span
                        className="block px-2 py-2 text-xs text-blue-600 hover:bg-gray-100 rounded-md cursor-pointer"
                        data-testid="link-platform-breakdown-view-all"
                      >
                        View all listings for this property →
                      </span>
                    </Link>
                  </div>
                </PopoverContent>
              </Popover>
            ) : null}
              </div>
            </div>
            <GuideNudgeBanner pageKey="property-detail" />
            <PropertySetupChecklist
              property={property}
              propertyPhoto={propertyPhotoQuery.data?.imageUrl}
              photoState={propertyPhotoLoadFailed ? "error" : getChecklistReadState(propertyPhotoQuery)}
              rooms={roomsQuery.data}
              roomsState={getChecklistReadState(roomsQuery)}
              knowledgeBase={knowledgeBaseQuery.data?.content}
              knowledgeBaseState={getChecklistReadState(knowledgeBaseQuery)}
              appliances={appliancesQuery.data}
              appliancesState={getChecklistReadState(appliancesQuery)}
              devices={checklistDevicesQuery.data}
              devicesState={checklistDevicesAllowed ? getChecklistReadState(checklistDevicesQuery) : "restricted"}
              documents={checklistDocumentsQuery.data}
              documentsState={getChecklistReadState(checklistDocumentsQuery)}
              expanded={setupChecklistExpanded}
              onExpandedChange={updateChecklistExpanded}
              onNavigate={handleChecklistNavigate}
              actions={<><ExplainerVideoButton pageKey="property-detail" /><GuideButton pageKey="property-detail" /></>}
              onRetry={(resource) => {
                if (resource === "photo") { setPropertyPhotoLoadFailed(false); void propertyPhotoQuery.refetch(); }
                if (resource === "rooms") void roomsQuery.refetch();
                if (resource === "knowledge") void knowledgeBaseQuery.refetch();
                if (resource === "appliances") void appliancesQuery.refetch();
                if (resource === "devices") void checklistDevicesQuery.refetch();
                if (resource === "documents") void checklistDocumentsQuery.refetch();
              }}
            />
          </div>
        </div>

        {property.parentPropertyId && (() => {
          const parentProp = (allPropertiesQuery.data || []).find((p) => p.id === property.parentPropertyId);
          return parentProp ? (
            <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 text-sm" data-testid="banner-child-property">
              <Building2 className="h-4 w-4 text-blue-500 shrink-0" />
              <span className="text-blue-800 dark:text-blue-200">This is a unit of</span>
              <Link href={`/app/properties/${parentProp.id}`}>
                <span className="text-blue-600 font-semibold hover:underline cursor-pointer" data-testid="link-parent-property">{parentProp.name}</span>
              </Link>
            </div>
          ) : null;
        })()}

        <Tabs
          value={activePropertyTab}
          onValueChange={(tab) => {
            if (!PROPERTY_REDIRECT_ONLY_TABS.has(tab)) {
              setActivePropertyTab(tab);
            }
          }}
          className="w-full"
        >
          <TabsList className="mb-6 flex w-full flex-nowrap justify-start overflow-x-auto rounded-none border-b bg-transparent p-0" data-testid="tabs-property" data-tour="property-detail-tabs">
            <TabsTrigger value="overview" className="shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-brand data-[state=active]:shadow-none" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="rooms" className="shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-brand data-[state=active]:shadow-none" data-testid="tab-rooms">Rooms</TabsTrigger>
            <TabsTrigger value="members" asChild className="shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-brand data-[state=active]:shadow-none" data-testid="tab-members" data-tour="property-detail-members"><Link href={`/app/members?propertyId=${propertyId}`}>Members</Link></TabsTrigger>
            <TabsTrigger value="maintenance" className="shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-brand data-[state=active]:shadow-none" data-testid="tab-maintenance" data-tour="property-detail-maintenance">Maintenance</TabsTrigger>
            <TabsTrigger value="appliances" className="shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-brand data-[state=active]:shadow-none" data-testid="tab-appliances" data-tour="property-detail-appliances">Appliances</TabsTrigger>
            <TabsTrigger value="devices" className="shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-brand data-[state=active]:shadow-none" data-testid="tab-devices" data-tour="property-detail-devices">Devices</TabsTrigger>
            <TabsTrigger value="documents" className="shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-brand data-[state=active]:shadow-none" data-testid="tab-documents" data-tour="property-detail-documents">Documents</TabsTrigger>
            <TabsTrigger value="knowledge-base" className="shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-brand data-[state=active]:shadow-none" data-testid="tab-knowledge-base" data-tour="property-detail-kb">Knowledge Base</TabsTrigger>
            {promptSettingsVisible ? <TabsTrigger value="prompts-settings" className="shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-brand data-[state=active]:shadow-none" data-testid="tab-prompts-settings">Prompts &amp; Settings</TabsTrigger> : null}
          </TabsList>

          <TabsContent value="overview">
            <div className="border rounded-lg p-6" data-tour="property-detail-overview">
              <div className="mb-7 grid grid-cols-1 gap-5 lg:grid-cols-2">
                <div id="property-photo" className="relative flex min-h-[220px] items-center justify-center overflow-hidden rounded-lg bg-slate-100" data-testid="area-property-image">
                  {propertyPhotoQuery.isLoading ? (
                    <Skeleton className="absolute inset-0 rounded-none" />
                  ) : propertyPhotoQuery.data?.imageUrl && !propertyPhotoLoadFailed ? (
                    <img src={propertyPhotoQuery.data.imageUrl} alt={`Property at ${property.address || property.name}`} onError={() => setPropertyPhotoLoadFailed(true)} className="absolute inset-0 h-full w-full object-cover" />
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-slate-400">
                      <Home className="h-16 w-16" aria-hidden />
                      {propertyPhotoQuery.isError || propertyPhotoLoadFailed ? <><span className="text-sm">Photo unavailable</span><Button type="button" size="sm" variant="outline" onClick={() => { setPropertyPhotoLoadFailed(false); void propertyPhotoQuery.refetch(); }}>Retry photo</Button></> : <span className="text-sm">No property photo yet</span>}
                    </div>
                  )}
                  {!readOnly ? (
                    <>
                      <input ref={propertyPhotoInputRef} type="file" accept={PROPERTY_IMAGE_MIME_TYPES.join(",")} className="sr-only" onChange={handlePropertyPhotoSelection} aria-label="Choose a property photo" />
                      <Button
                        type="button"
                        variant="secondary"
                        className="absolute bottom-3 right-3 shadow-sm"
                        disabled={propertyPhotoUploading}
                        onClick={() => propertyPhotoInputRef.current?.click()}
                        data-testid="button-upload-property-photo"
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        {propertyPhotoUploading ? "Uploading…" : propertyPhotoQuery.data?.imageUrl ? "Replace photo" : "Upload image"}
                      </Button>
                    </>
                  ) : null}
                  {propertyPhotoUploading ? <div className="absolute inset-0 bg-slate-900/10" aria-live="polite"><span className="sr-only">Uploading property photo</span></div> : null}
                </div>
                <div className="min-h-[220px] overflow-hidden rounded-lg bg-slate-100" data-testid="area-property-map">
                  {property.latitude != null && property.longitude != null ? (
                    <PropertyMap properties={[property]} />
                  ) : (
                    <div className="flex h-full min-h-[220px] items-center justify-center px-6 text-center text-sm text-slate-500">
                      Add a property location to display its map.
                    </div>
                  )}
                </div>
              </div>
              <fieldset disabled={readOnly} id="property-overview-fields" className="grid min-w-0 grid-cols-1 items-stretch gap-6 lg:grid-cols-2">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Property Title</label>
                    <Input
                      defaultValue={property.name}
                      onBlur={(e) => handleFieldBlur("name", e.target.value)}
                      data-testid="input-property-title"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Property Type</label>
                    <Select
                      value={property.managementType || "padsplit"}
                      onValueChange={(value) => updateMutation.mutate({ managementType: value } as Partial<Property>)}
                      disabled={readOnly}
                    >
                      <SelectTrigger data-testid="select-management-type-overview"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="padsplit">PadSplit</SelectItem>
                        <SelectItem value="self_managed">Direct Managed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Location</label>
                    <AddressAutocomplete
                      value={displayAddress}
                      onSelect={(address, coords, zipCode, _city, state, _streetAddress, country) => {
                        updateMutation.mutate({
                          address,
                          ...(coords ? { latitude: coords.lat, longitude: coords.lng } : {}),
                          ...(state ? { state } : {}),
                          ...(country ? { country } : {}),
                          ...(zipCode ? { postalCode: zipCode } : {}),
                        } as Partial<Property>);
                        if (zipCode) {
                          apiRequest("PATCH", `/api/app/underwriting/${property.id}`, { zipCode }).catch(() => {});
                        }
                      }}
                      onBlurChange={(address) => {
                        if (address !== (property.address || "")) {
                          updateMutation.mutate({ address } as Partial<Property>);
                        }
                      }}
                      data-testid="input-property-location"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
                    {(() => {
                      // Active members are those with a room assignment on this
                      // property — matches the server-side propertyHasActiveMembers
                      // check (members.status='active' is already enforced by the
                      // endpoint; roomId!=null mirrors the rooms-join guard).
                      const hasActiveMembers = (propertyMembersQuery.data?.filter(m => m.roomId != null).length ?? 0) > 0;
                      // Task #2900 — Lifecycle order: Underwriting → Acquisition
                      // → Renovation → Launch → Active → Archive → Sold → Disposed.
                      // "active" is rendered separately so it stays enabled even
                      // when members are assigned (you don't block staying active).
                      const nonActiveStatuses = [
                        { value: "underwriting", label: "Underwriting" },
                        { value: "acquisition", label: "Acquisition" },
                        { value: "renovations", label: "Renovation" },
                        { value: "launch", label: "Launch" },
                      ];
                      const terminalStatuses = [
                        { value: "archive", label: "Archive" },
                        { value: "sold", label: "Sold" },
                        { value: "disposed", label: "Disposed" },
                      ];
                      return (
                        <>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div>
                                  <Select value={currentStatus} onValueChange={handleStatusChange} disabled={readOnly}>
                                    <SelectTrigger data-testid="select-property-status-detail">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {nonActiveStatuses.map(({ value, label }) => (
                                        <SelectItem
                                          key={value}
                                          value={value}
                                          disabled={hasActiveMembers}
                                          data-testid={`select-status-option-${value}`}
                                        >
                                          {label}
                                        </SelectItem>
                                      ))}
                                      <SelectItem value="active" data-testid="select-status-option-active">Active</SelectItem>
                                      {terminalStatuses.map(({ value, label }) => (
                                        <SelectItem
                                          key={value}
                                          value={value}
                                          disabled={hasActiveMembers}
                                          data-testid={`select-status-option-${value}`}
                                        >
                                          {label}
                                        </SelectItem>
                                      ))}
                                      <SelectSeparator />
                                      <SelectItem
                                        value="__delete__"
                                        className="text-red-600 focus:text-red-600 dark:text-red-400 dark:focus:text-red-400"
                                        data-testid="select-status-option-delete"
                                      >
                                        Delete
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              </TooltipTrigger>
                              {hasActiveMembers && (
                                <TooltipContent side="bottom">
                                  <p className="max-w-xs text-xs">This property has active members assigned. Remove or reassign all members before changing the status.</p>
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </TooltipProvider>
                          {statusError && (
                            <p className="mt-1 text-sm text-red-600" data-testid="text-status-error">{statusError}</p>
                          )}
                          <Dialog
                            open={deleteDialogOpen}
                            onOpenChange={(open) => {
                              if (deletePropertyMutation.isPending) return;
                              setDeleteDialogOpen(open);
                              if (!open) setDeleteConfirmText("");
                            }}
                          >
                            <DialogContent
                              onInteractOutside={(e) => { if (deletePropertyMutation.isPending) e.preventDefault(); }}
                              onEscapeKeyDown={(e) => { if (deletePropertyMutation.isPending) e.preventDefault(); }}
                              onPointerDownOutside={(e) => { if (deletePropertyMutation.isPending) e.preventDefault(); }}
                              data-testid="dialog-delete-property"
                            >
                              <DialogHeader>
                                <DialogTitle>Confirm Delete</DialogTitle>
                                <DialogDescription>
                                  This permanently deletes this property and all of its data. This action cannot be undone. Type <span className="font-semibold">delete</span> below to confirm.
                                </DialogDescription>
                              </DialogHeader>
                              <Input
                                value={deleteConfirmText}
                                onChange={(e) => setDeleteConfirmText(e.target.value)}
                                placeholder="delete"
                                autoComplete="off"
                                disabled={deletePropertyMutation.isPending}
                                data-testid="input-delete-confirm"
                              />
                              <DialogFooter>
                                <Button
                                  variant="outline"
                                  onClick={() => { setDeleteDialogOpen(false); setDeleteConfirmText(""); }}
                                  disabled={deletePropertyMutation.isPending}
                                  data-testid="button-cancel-delete-property"
                                >
                                  Cancel
                                </Button>
                                <Button
                                  variant="destructive"
                                  onClick={() => deletePropertyMutation.mutate()}
                                  disabled={deleteConfirmText.toLowerCase() !== "delete" || deletePropertyMutation.isPending}
                                  data-testid="button-confirm-delete-property"
                                >
                                  {deletePropertyMutation.isPending ? "Deleting..." : "Delete"}
                                </Button>
                              </DialogFooter>
                            </DialogContent>
                          </Dialog>
                        </>
                      );
                    })()}
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Room Count</label>
                      <Input
                        type="number"
                        defaultValue={property.roomCount || 0}
                        onBlur={(e) => handleFieldBlur("roomCount", e.target.value)}
                        data-testid="input-property-room-count"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Bathroom Count</label>
                      <Input
                        type="number"
                        defaultValue={property.bathroomCount || 0}
                        onBlur={(e) => handleFieldBlur("bathroomCount", e.target.value)}
                        data-testid="input-property-bathroom-count"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">FloorPlan Link</label>
                    <Input
                      defaultValue={property.floorPlanUrl || ""}
                      onBlur={(e) => handleFieldBlur("floorPlanUrl", e.target.value)}
                      placeholder="https://..."
                      className="text-sm"
                      data-testid="input-detail-floorplan-link"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">3D Tour Link</label>
                    <Input
                      defaultValue={property.tourUrl || ""}
                      onBlur={(e) => handleFieldBlur("tourUrl", e.target.value)}
                      placeholder="https://..."
                      className="text-sm"
                      data-testid="input-detail-tour-link"
                    />
                  </div>
                  <div>
                    <label htmlFor="property-video-walkthrough" className="block text-sm font-semibold text-gray-700 mb-1">Property Video Walkthrough</label>
                    <div className="flex gap-2">
                      <Input
                        key={property.videoWalkthroughUrl || ""}
                        id="property-video-walkthrough"
                        defaultValue={property.videoWalkthroughUrl || ""}
                        type="url"
                        placeholder="https://..."
                        aria-invalid={Boolean(videoUrlError)}
                        aria-describedby={videoUrlError ? "property-video-error" : undefined}
                        onBlur={(e) => {
                          const value = e.target.value.trim();
                          setVideoUrlError(null);
                          if (!value) {
                            if (property.videoWalkthroughUrl) updateMutation.mutate({ videoWalkthroughUrl: null } as Partial<Property>);
                            return;
                          }
                          if (!isSafePropertyVideoUrl(value)) {
                            setVideoUrlError("Enter a valid HTTP or HTTPS video link.");
                            return;
                          }
                          if (value !== property.videoWalkthroughUrl) updateMutation.mutate({ videoWalkthroughUrl: value } as Partial<Property>);
                        }}
                        className="text-sm"
                        data-testid="input-property-video-walkthrough"
                      />
                      {property.videoWalkthroughUrl && isSafePropertyVideoUrl(property.videoWalkthroughUrl) ? (
                        <a href={property.videoWalkthroughUrl} target="_blank" rel="noopener noreferrer" aria-label="Open video walkthrough in a new tab" className="inline-flex h-10 shrink-0 items-center rounded-md border border-input bg-background px-3 text-sm hover:bg-accent">
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      ) : null}
                    </div>
                    {videoUrlError ? <p id="property-video-error" className="mt-1 text-xs text-red-600" role="alert">{videoUrlError}</p> : null}
                  </div>
                </div>
                <div className="flex min-h-full flex-col">
                  <label className="mb-1 block text-sm font-semibold text-gray-700">Description</label>
                  <Textarea
                    defaultValue={property.description || ""}
                    onBlur={(e) => handleFieldBlur("description", e.target.value)}
                    className="min-h-[340px] flex-1 resize-y"
                    data-testid="textarea-property-description"
                  />
                </div>
              </fieldset>

            </div>
            <fieldset disabled={readOnly} style={{ display: "contents" }}>
              <div className="mt-6 rounded-lg border p-6" data-testid="property-details-card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-testid="text-details-heading">Property Details</h3>
                <div className="grid grid-cols-1 gap-4 mb-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Property Group*</label>
                    <PropertyDetailGroup
                      property={property}
                      readOnly={readOnly}
                      busy={updateMutation.isPending}
                      onSessionExpired={() => navigate("/app/login")}
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-1">
                    <label className="block text-xs font-semibold text-gray-700">PadSplit Property ID</label>
                    {property.padsplitPropertyId && (
                      <a
                        href={`https://www.padsplit.com/host/listing/${property.padsplitPropertyId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline flex items-center gap-0.5"
                        data-testid="link-open-on-padsplit"
                      >
                        Open on PadSplit ↗
                      </a>
                    )}
                  </div>
                  {property.padsplitPropertyId ? (
                    <p className="text-sm text-gray-800" data-testid="text-padsplit-property-id">
                      {property.padsplitPropertyId}
                    </p>
                  ) : (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <p className="text-sm text-gray-400 italic cursor-default" data-testid="text-padsplit-property-id-empty">
                            Not yet synced
                          </p>
                        </TooltipTrigger>
                        <TooltipContent>
                          Please share this property with your assistant in order to sync.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
                {(propertyOwnersQuery.data?.length ?? 0) > 1 && (
                  <div className="mb-4">
                    <label className="block text-xs font-semibold text-gray-700 mb-1">PadSplit Message Sender</label>
                    <Select
                      value={property.padsplitSenderUserId != null ? String(property.padsplitSenderUserId) : "default"}
                      onValueChange={(value) =>
                        updateMutation.mutate({
                          padsplitSenderUserId: value === "default" ? null : parseInt(value),
                        } as Partial<Property>)
                      }
                      disabled={readOnly}
                    >
                      <SelectTrigger className="h-8 text-sm" data-testid="select-padsplit-sender">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="default">Automatic (member's linked host)</SelectItem>
                        {(propertyOwnersQuery.data ?? []).map((owner) => (
                          <SelectItem key={owner.id} value={String(owner.id)} data-testid={`option-padsplit-sender-${owner.id}`}>
                            {owner.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">
                      Which account's PadSplit connection sends automated messages for this property.
                    </p>
                  </div>
                )}
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Booking Auto-Approval Window</label>
                  <Select
                    value={String(property.autoApprovalWindowHours ?? 24)}
                    onValueChange={(value) =>
                      updateMutation.mutate({
                        autoApprovalWindowHours: parseInt(value),
                      } as Partial<Property>)
                    }
                    disabled={readOnly}
                  >
                    <SelectTrigger className="h-8 text-sm" data-testid="select-auto-approval-window">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="24" data-testid="option-auto-approval-window-24">24 hours</SelectItem>
                      <SelectItem value="48" data-testid="option-auto-approval-window-48">48 hours</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    How long PadSplit waits before auto-approving a booking request. Must match this
                    property's setting on PadSplit — it times the "Booking Form Not Completed" reminder rule.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <label className="block text-xs font-semibold text-gray-700">Parent Property (Multi-Unit)</label>
                      <Dialog open={createUnitOpen} onOpenChange={(open) => {
                        if (open) {
                          setCreateUnitName(`${property.name} (copy)`);
                          setCreateUnitAddress(property.address || "");
                          setCreateUnitRooms(String(property.roomCount || 0));
                          setCreateUnitBaths(String(property.bathroomCount || 0));
                        }
                        setCreateUnitOpen(open);
                      }}>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm" className="h-5 text-[10px] px-2 py-0" data-testid="button-create-another-unit">
                            <Plus className="h-3 w-3 mr-0.5" />
                            Create Another Unit
                          </Button>
                        </DialogTrigger>
                        <DialogContent
                          className="max-w-md"
                          onInteractOutside={(e) => {
                            const target = e.target as HTMLElement | null;
                            if (target?.closest?.('[data-address-autocomplete-portal]')) {
                              e.preventDefault();
                            }
                          }}
                        >
                          <DialogHeader>
                            <DialogTitle>Create Another Unit</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 pt-2">
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">Unit Name</label>
                              <Input
                                value={createUnitName}
                                onChange={(e) => setCreateUnitName(e.target.value)}
                                placeholder="e.g. 123 Main St Unit B"
                                data-testid="input-create-unit-name"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">Address</label>
                              <AddressAutocomplete
                                value={createUnitAddress}
                                onChange={(address) => setCreateUnitAddress(address)}
                                onSelect={(address) => setCreateUnitAddress(address)}
                                placeholder="Street address"
                                data-testid="input-create-unit-address"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1">Rooms</label>
                                <Input
                                  type="number"
                                  min="0"
                                  value={createUnitRooms}
                                  onChange={(e) => setCreateUnitRooms(e.target.value)}
                                  data-testid="input-create-unit-rooms"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1">Bathrooms</label>
                                <Input
                                  type="number"
                                  min="0"
                                  value={createUnitBaths}
                                  onChange={(e) => setCreateUnitBaths(e.target.value)}
                                  data-testid="input-create-unit-baths"
                                />
                              </div>
                            </div>
                            <div className="flex justify-end gap-2 pt-1">
                              <Button variant="outline" size="sm" onClick={() => setCreateUnitOpen(false)} data-testid="button-create-unit-cancel">
                                Cancel
                              </Button>
                              <Button
                                size="sm"
                                disabled={!createUnitName.trim() || createUnitMutation.isPending}
                                onClick={() => createUnitMutation.mutate(property)}
                                data-testid="button-create-unit-submit"
                              >
                                {createUnitMutation.isPending ? "Creating..." : "Create Unit"}
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                    <Select
                      value={property.parentPropertyId ? String(property.parentPropertyId) : "none"}
                      onValueChange={(val) => {
                        const parentId = val === "none" ? null : parseInt(val);
                        updateMutation.mutate({ parentPropertyId: parentId } as Partial<Property>);
                      }}
                    >
                      <SelectTrigger className="text-sm" data-testid="select-parent-property">
                        <SelectValue placeholder="None (standalone)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None (standalone)</SelectItem>
                        {(allPropertiesQuery.data || [])
                          .filter((p) => p.id !== propertyId && !p.parentPropertyId)
                          .map((p) => (
                            <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                          ))
                        }
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-400 mt-1">Link this property as a unit under a parent property for combined financials.</p>
                  </div>
                  {(() => {
                    const childProperties = (allPropertiesQuery.data || []).filter((p) => p.parentPropertyId === propertyId && p.id !== propertyId);
                    if (childProperties.length === 0) return null;
                    return (
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Child Properties ({childProperties.length})</label>
                        <div className="space-y-1">
                          {childProperties.map((child) => (
                            <Link key={child.id} href={`/app/properties/${child.id}`}>
                              <div className="flex items-center gap-2 p-2 rounded border border-gray-100 bg-gray-50 hover:bg-gray-100 cursor-pointer text-sm" data-testid={`child-property-row-${child.id}`}>
                                <Home className="h-3.5 w-3.5 text-gray-400" />
                                <span className="text-gray-800 font-medium">{child.name}</span>
                                <span className="text-xs text-gray-400 ml-auto">{child.status}</span>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {(currentStatus === "sold" || currentStatus === "disposed") && (
                    <>
                      <DatePickerField
                        label={currentStatus === "disposed" ? "Disposition Date" : "Sale Date"}
                        value={property.saleDate || ""}
                        onChange={(val) => updateMutation.mutate({ saleDate: val } as Partial<Property>)}
                        testId="input-detail-sale-date"
                      />
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                          {currentStatus === "disposed" ? "Disposition Amount" : "Sale Price"}
                        </label>
                        <Input
                          defaultValue={property.salePrice || ""}
                          key={property.salePrice || ""}
                          onBlur={(e) => handleFieldBlur("salePrice", e.target.value)}
                          placeholder="$0"
                          className="text-sm"
                          data-testid="input-detail-sale-price"
                        />
                      </div>
                    </>
                  )}
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Time Zone</label>
                  <div className="w-1/2">
                    <Select
                      value={property.timezone || "__auto__"}
                      onValueChange={(v) => {
                        updateMutation.mutate({ timezone: v === "__auto__" ? null : v } as Partial<Property>);
                      }}
                    >
                      <SelectTrigger className="text-sm" data-testid="select-detail-timezone">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__auto__">Automatic (based on location)</SelectItem>
                        {ACCOUNT_TIMEZONES.map((tz) => (
                          <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Used to schedule recurring messages in this property's local time.
                    {!property.timezone && guessTimezoneFromState(property.state)
                      ? ` Currently using ${guessTimezoneFromState(property.state)} from the property's location.`
                      : ""}
                  </p>
                </div>
              </div>

            <section id="property-wifi" className="mt-6 rounded-lg border p-6" data-testid="property-wifi-card">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">Wi-Fi Details</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                <div>
                  <label className="mb-1 block text-sm font-semibold text-gray-700">Wi-Fi ISP Provider</label>
                  <Select value={property.ispProvider || "none"} onValueChange={(v) => updateMutation.mutate({ ispProvider: v === "none" ? null : v } as Partial<Property>)} disabled={readOnly}>
                    <SelectTrigger className="text-sm" data-testid="select-detail-isp-provider"><SelectValue placeholder="None / Unknown" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None / Unknown</SelectItem>
                      {ISP_OPTIONS.map((isp) => <SelectItem key={isp.value} value={isp.value}>{isp.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-xs text-gray-500">Used to link the ISP outage status page from message view.</p>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-semibold text-gray-700">Wi-Fi Name</label>
                  <Input defaultValue={property.wifiName || ""} onBlur={(e) => handleFieldBlur("wifiName", e.target.value)} placeholder="Network SSID" className="text-sm" data-testid="input-detail-wifi-name" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-semibold text-gray-700">Wi-Fi Password</label>
                  <Input defaultValue={property.wifiPassword || ""} onBlur={(e) => handleFieldBlur("wifiPassword", e.target.value)} placeholder="Password" className="text-sm" data-testid="input-detail-wifi-password" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-semibold text-gray-700">ISP Outage URL</label>
                  <Input defaultValue={property.ispOutageUrl || ""} onBlur={(e) => handleFieldBlur("ispOutageUrl", e.target.value)} placeholder="https://…/outages" className="text-sm" data-testid="input-detail-isp-outage-url" />
                  <p className="mt-1 text-xs text-gray-500">Residents can check outage status from their dashboard.</p>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-semibold text-gray-700">ISP Label</label>
                  <Input defaultValue={property.ispLabel || ""} onBlur={(e) => handleFieldBlur("ispLabel", e.target.value)} placeholder="e.g. Xfinity" className="text-sm" data-testid="input-detail-isp-label" />
                </div>
              </div>
            </section>
            <section id="property-waste" className="mt-6 rounded-lg border p-6" data-testid="property-waste-card">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">Waste Pickup</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-semibold text-gray-700">Trash Pickup Day</label>
                  <Input defaultValue={property.trashPickupDay || ""} onBlur={(e) => handleFieldBlur("trashPickupDay", e.target.value)} placeholder="e.g. Monday" className="text-sm" data-testid="input-detail-trash-day" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-semibold text-gray-700">Recycling Pickup Day</label>
                  <Input defaultValue={property.recyclingPickupDay || ""} onBlur={(e) => handleFieldBlur("recyclingPickupDay", e.target.value)} placeholder="e.g. Thursday (bi-weekly)" className="text-sm" data-testid="input-detail-recycling-day" />
                </div>
              </div>
            </section>
            <section className="mt-6 rounded-lg border p-6" data-testid="property-closing-team-card">
              <ClosingTeamPanel propertyId={property.id} readOnly={readOnly} />
            </section>
            {(property.managementType || "padsplit") === "self_managed" ? (
              <section className="mt-6 rounded-lg border p-6" data-testid="self-managed-details-section">
                <h3 className="mb-4 text-lg font-semibold text-gray-900" data-testid="text-self-managed-heading">Self-Managed Property Details</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-semibold text-gray-700">House Rules</label>
                    <Textarea
                      defaultValue={property.houseRules || ""}
                      onBlur={(e) => handleFieldBlur("houseRules", e.target.value)}
                      placeholder="Quiet hours, guests, shared spaces…"
                      rows={5}
                      className="text-sm"
                      data-testid="input-detail-house-rules"
                    />
                    <p className="mt-1 text-xs text-gray-500">Shown on each resident's House Guide.</p>
                  </div>
                  <HouseGuideDocuments propertyId={propertyId} documents={Array.isArray(property.houseDocuments) ? property.houseDocuments : []} />
                </div>
                <div className="mt-6">
                  <ResidentSentiment propertyId={propertyId} />
                </div>
              </section>
            ) : null}
            </fieldset>
          </TabsContent>

          <TabsContent value="rooms">
            <fieldset disabled={readOnly} style={{ display: "contents" }}>
            <div className="border rounded-lg p-6" data-tour="property-detail-rooms">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4 flex-wrap">
                  <h3 className="text-lg font-semibold text-gray-900" data-testid="text-rooms-heading">Rooms ({rooms.length})</h3>
                  {billableRooms.length > 0 && (
                    <div className="flex items-center gap-3 text-sm text-muted-foreground" data-testid="property-occupancy-summary">
                      <span className="flex items-center gap-1">
                        <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                        <span data-testid="text-property-occupied-count">{occupancyStats.occupiedRooms} occupied</span>
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="inline-block w-2 h-2 rounded-full bg-amber-400" />
                        <span data-testid="text-property-vacant-count">{occupancyStats.vacantRooms} vacant</span>
                      </span>
                      <span className="font-medium text-foreground" data-testid="text-property-occupancy-pct">
                        {occupancyStats.occupancyPct}% occupancy
                      </span>
                      {excludeInactive && occupancyStats.inactiveRooms > 0 && (
                        <span className="text-xs text-blue-600" data-testid="text-property-excluding-inactive">
                          Excl. {occupancyStats.inactiveRooms} inactive
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex rounded-md border overflow-hidden" data-testid="toggle-rooms-view">
                    <button
                      className={`px-3 py-1.5 text-xs font-medium transition-colors ${roomsView === "data" ? "bg-brand text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                      onClick={() => setRoomsView("data")}
                      data-testid="button-view-room-data"
                    >
                      Room Data
                    </button>
                    <button
                      className={`px-3 py-1.5 text-xs font-medium transition-colors ${roomsView === "images" ? "bg-brand text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                      onClick={() => setRoomsView("images")}
                      data-testid="button-view-image-generation"
                      data-tour="property-detail-image-generator"
                    >
                      Image Generation
                    </button>
                  </div>
                  {roomsView === "images" && promptSettingsVisible && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setActivePropertyTab("prompts-settings")}
                      data-testid="button-prompts-settings"
                    >
                      <BookOpen className="h-4 w-4 mr-1" /> Prompts &amp; Settings
                    </Button>
                  )}
                  {property.padsplitPropertyId ? (
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-not-allowed" tabIndex={0}>
                            <Button size="sm" className="bg-brand opacity-50 pointer-events-none" data-testid="button-add-room" disabled>
                              <Plus className="h-4 w-4 mr-1" /> Add Room
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[260px]">
                          Rooms on PadSplit-linked properties must sync from PadSplit. Use the PadSplit Sync tab to pull in rooms.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                  <Dialog open={addRoomOpen} onOpenChange={(next) => {
                    setAddRoomOpen(next);
                  }}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-brand" data-testid="button-add-room">
                        <Plus className="h-4 w-4 mr-1" /> Add Room
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add Room</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={(e) => { e.preventDefault(); if (newRoomNumber.trim()) addRoomMutation.mutate({ roomNumber: newRoomNumber.trim(), roomType: newRoomType }); }} className="space-y-4 mt-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Room Number / Name</label>
                          <Input
                            value={newRoomNumber}
                            onChange={(e) => setNewRoomNumber(e.target.value)}
                            placeholder="e.g. Room 1, Master Suite, Bath 3"
                            data-testid="input-room-number"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Room Type</label>
                          <Select value={newRoomType} onValueChange={setNewRoomType}>
                            <SelectTrigger data-testid="select-new-room-type">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {property.managementType === "padsplit" ? (
                                <TooltipProvider delayDuration={0}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="cursor-not-allowed flex items-center">
                                        <SelectItem value="bedroom" disabled className="pointer-events-none opacity-50 flex-1">
                                          <span className="flex items-center gap-1.5">
                                            Bedroom
                                            <Info className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                                          </span>
                                        </SelectItem>
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="max-w-[220px]">
                                      Please add the bedroom via PadSplit and then sync rooms from the PadSplit sync tab
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : (
                                <SelectItem value="bedroom">Bedroom</SelectItem>
                              )}
                              <SelectItem value="bathroom">Bathroom</SelectItem>
                              <SelectItem value="common_space">Common Space</SelectItem>
                              <SelectItem value="exterior">Exterior</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button type="submit" className="w-full bg-brand" disabled={addRoomMutation.isPending} data-testid="button-submit-room">
                          {addRoomMutation.isPending ? "Adding..." : "Add Room"}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                  )}
                </div>
              </div>

              {/* Category filter strip */}
              <div className="flex gap-1 mb-4 flex-wrap" data-testid="category-filter-strip">
                {(["all", "bedroom", "bathroom", "common_space", "exterior"] as const).map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setRoomCategory(cat)}
                    data-testid={`filter-category-${cat}`}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors capitalize ${
                      roomCategory === cat
                        ? "bg-brand text-white border-brand"
                        : "bg-white text-gray-600 border-gray-200 hover:border-brand hover:text-brand"
                    }`}
                  >
                    {cat === "all" ? "All" : cat === "common_space" ? "Common Space" : cat === "exterior" ? "Exterior" : cat === "bedroom" ? "Bedrooms" : "Bathrooms"}
                  </button>
                ))}
              </div>

              {/* Variants dialog */}
              <RoomVariantsDialog
                roomId={variantsRoomId}
                roomName={orderedRooms.find(room => room.id === variantsRoomId)?.roomNumber ?? "Room"}
                propertyId={propertyId}
                readOnly={readOnly}
                onClose={() => { setImagePreviewUrl(null); setVariantsRoomId(null); }}
                restoreFocus={() => variantsTriggerRef.current?.focus()}
                onPreview={(url, trigger) => { previewTriggerRef.current = trigger; setImagePreviewUrl(url); }}
              />

              {/* Image preview lightbox */}
              {/* Keep both dialog roots mounted so portal/focus cleanup can finish
                  before another dialog opens; do not change shared modal behavior. */}
                <Dialog open={!!imagePreviewUrl} onOpenChange={(open) => { if (!open) setImagePreviewUrl(null); }}>
                  <DialogContent className="max-w-4xl p-2" onCloseAutoFocus={(event) => {
                    if (previewTriggerRef.current?.isConnected) {
                      event.preventDefault();
                      previewTriggerRef.current.focus();
                    }
                  }}>
                    <DialogTitle className="sr-only">Image preview</DialogTitle>
                    <DialogDescription className="sr-only">Full-size room image</DialogDescription>
                    {imagePreviewUrl && failedImageUrls.has(imagePreviewUrl) ? (
                      <div className="w-full aspect-video rounded bg-gray-50 flex items-center justify-center text-sm text-gray-400" data-testid="img-preview-unavailable">
                        Image unavailable
                      </div>
                    ) : (
                      <img
                        src={imagePreviewUrl ?? undefined}
                        alt="Preview"
                        className="w-full rounded"
                        onError={() => imagePreviewUrl && markImageFailed(imagePreviewUrl)}
                      />
                    )}
                  </DialogContent>
                </Dialog>

              {roomsQuery.isLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
                </div>
              ) : rooms.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">No rooms added yet</p>
                </div>
              ) : roomsView === "images" ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Room</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Input Image</TableHead>
                        <TableHead>Prompt</TableHead>
                        <TableHead>Output Image</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredRooms.map((room) => {
                        const imagePrompts = imagePromptsQuery.data || [];
                        const selectedPromptId = (room as Room & { promptId?: number }).promptId;
                        const outputImageUrl = (room as Room & { outputImageUrl?: string }).outputImageUrl;
                        const imageStatus = (room as Room & { imageStatus?: string }).imageStatus || "pending";
                        return (
                          <TableRow key={room.id} data-testid={`row-image-room-${room.id}`}>
                            <TableCell className="font-medium text-sm" data-testid={`text-image-room-number-${room.id}`}>
                              {(room.roomType === "common" || room.roomType === "common_area" || room.roomType === "common_space" || room.roomType === "exterior") ? (
                                <Input
                                  defaultValue={room.roomNumber || ""}
                                  onBlur={(e) => {
                                    const trimmed = e.target.value.trim();
                                    if (trimmed && trimmed !== (room.roomNumber || "")) {
                                      updateRoomMutation.mutate({ roomId: room.id, data: { roomNumber: trimmed } as Partial<Room> });
                                    }
                                  }}
                                  placeholder="Label..."
                                  className="text-sm h-8 w-32"
                                  data-testid={`input-image-room-number-${room.id}`}
                                />
                              ) : (
                                room.roomNumber
                              )}
                            </TableCell>
                            <TableCell>
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 capitalize" data-testid={`badge-image-room-type-${room.id}`}>
                                {room.roomType || "bedroom"}
                              </span>
                            </TableCell>
                            <TableCell>
                              {room.inputImageUrl ? (
                                <div className="flex flex-col items-start gap-1">
                                  {failedImageUrls.has(room.inputImageUrl) ? (
                                    <div
                                      className="h-14 w-20 rounded border bg-gray-50 flex items-center justify-center text-center text-[9px] leading-tight text-gray-400 px-1"
                                      data-testid={`img-input-unavailable-${room.id}`}
                                    >
                                      Image unavailable
                                    </div>
                                  ) : (
                                    <img
                                      src={room.inputImageUrl}
                                      alt="Input"
                                      className="h-14 w-20 object-cover rounded border cursor-pointer"
                                      onClick={() => setImagePreviewUrl(room.inputImageUrl!)}
                                      onError={() => markImageFailed(room.inputImageUrl!)}
                                      data-testid={`img-input-${room.id}`}
                                    />
                                  )}
                                  <div className="flex items-center gap-2 text-[10px]">
                                    <button
                                      className="text-brand hover:underline"
                                      disabled={readOnly || roomImageUploadingIds.has(room.id)}
                                      onClick={() => {
                                        const input = document.createElement("input");
                                        input.type = "file";
                                        input.accept = PROPERTY_IMAGE_MIME_TYPES.join(",");
                                        input.onchange = async (ev) => {
                                          const file = (ev.target as HTMLInputElement).files?.[0];
                                          if (file) await handleImageUpload(room.id, file);
                                        };
                                        input.click();
                                      }}
                                      data-testid={`button-replace-input-${room.id}`}
                                    >
                                      {roomImageUploadingIds.has(room.id) ? "Uploading…" : "Replace"}
                                    </button>
                                    <button
                                      className="text-red-500 hover:underline disabled:opacity-40"
                                      disabled={readOnly || updateRoomMutation.isPending}
                                      onClick={() => updateRoomMutation.mutate({ roomId: room.id, data: { inputImageUrl: null } as Partial<Room> })}
                                      data-testid={`button-remove-input-${room.id}`}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <button
                                  className="h-14 w-20 rounded border-2 border-dashed border-gray-200 flex items-center justify-center text-xs text-gray-400 hover:border-brand hover:text-brand transition-colors disabled:cursor-wait disabled:opacity-50"
                                  disabled={readOnly || roomImageUploadingIds.has(room.id)}
                                  onClick={() => {
                                    const input = document.createElement("input");
                                    input.type = "file";
                                    input.accept = PROPERTY_IMAGE_MIME_TYPES.join(",");
                                    input.onchange = async (ev) => {
                                      const file = (ev.target as HTMLInputElement).files?.[0];
                                      if (file) await handleImageUpload(room.id, file);
                                    };
                                    input.click();
                                  }}
                                  data-testid={`button-upload-input-${room.id}`}
                                >
                                  {roomImageUploadingIds.has(room.id) ? "Uploading…" : "Upload"}
                                </button>
                              )}
                            </TableCell>
                            <TableCell>
                              <Select
                                value={selectedPromptId ? String(selectedPromptId) : "none"}
                                onValueChange={(val) => {
                                  updateRoomMutation.mutate({ roomId: room.id, data: { promptId: val === "none" ? null : Number(val) } as Partial<Room> });
                                }}
                              >
                                <SelectTrigger className="h-8 text-xs w-36" data-testid={`select-prompt-${room.id}`}>
                                  <SelectValue placeholder="No prompt" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">No prompt</SelectItem>
                                  {imagePrompts.map(p => (
                                    <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              {outputImageUrl && failedImageUrls.has(outputImageUrl) ? (
                                <div className="h-14 w-20 rounded border bg-gray-50 flex items-center justify-center text-center text-[9px] leading-tight text-gray-400 px-1" data-testid={`img-output-unavailable-${room.id}`}>
                                  Image unavailable
                                </div>
                              ) : outputImageUrl ? (
                                <img
                                  src={outputImageUrl}
                                  alt="Output"
                                  className="h-14 w-20 object-cover rounded border cursor-pointer"
                                  onClick={() => setImagePreviewUrl(outputImageUrl)}
                                  onError={() => markImageFailed(outputImageUrl)}
                                  data-testid={`img-output-${room.id}`}
                                />
                              ) : (
                                <div className="h-14 w-20 rounded border bg-gray-50 flex items-center justify-center text-xs text-gray-400" data-testid={`placeholder-output-${room.id}`}>
                                  {imageStatus === "processing" ? "Generating..." : "No output"}
                                </div>
                              )}
                            </TableCell>
                            <TableCell>
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  imageStatus === "completed" ? "bg-green-100 text-green-700" :
                                  imageStatus === "processing" ? "bg-blue-100 text-blue-700" :
                                  imageStatus === "error" ? "bg-red-100 text-red-700" :
                                  "bg-gray-100 text-gray-600"
                                }`}
                                data-testid={`badge-image-status-${room.id}`}
                              >
                                {imageStatus}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                <Button
                                  size="sm"
                                  className="text-xs h-7 bg-brand"
                                  disabled={readOnly || generateRoomImageMutation.isPending}
                                  onClick={() => generateRoomImageMutation.mutate({ roomId: room.id, promptId: selectedPromptId ?? undefined })}
                                  data-testid={`button-generate-${room.id}`}
                                >
                                  Generate
                                </Button>
                                {outputImageUrl && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-xs h-7"
                                    disabled={readOnly || createVariantMutation.isPending}
                                    onClick={() => createVariantMutation.mutate(room.id)}
                                    data-testid={`button-save-variant-${room.id}`}
                                  >
                                    Save Variant
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-xs h-7"
                                  onClick={(event) => { variantsTriggerRef.current = event.currentTarget; setVariantsRoomId(room.id); }}
                                  data-testid={`button-view-variants-${room.id}`}
                                >
                                  Variants
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <p className="text-xs text-muted-foreground mb-2">{PADSPLIT_ROOM_MANAGED_MESSAGE}</p>
                  <Table className="table-fixed min-w-[1800px]" data-testid="table-room-data">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-28">Room</TableHead>
                        <TableHead className="w-32">Type</TableHead>
                        <TableHead className="w-40">Member</TableHead>
                        <TableHead className="w-44">Associated Bathroom</TableHead>
                        <TableHead className="w-36">Status</TableHead>
                        <TableHead className="w-[344px]">Room Title</TableHead>
                        <TableHead className="w-56">Room Description</TableHead>
                        <TableHead className="w-20">Images</TableHead>
                        <TableHead className="w-28">PadSplit Room ID</TableHead>
                        <TableHead className="w-32">Dimensions</TableHead>
                        <TableHead className="w-44">Notes</TableHead>
                        <TableHead className="w-28">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredRooms.map((room) => {
                        const isShadow = !!room.shadowOfRoomId;
                        const parentRoom = isShadow ? rooms.find(r => r.id === room.shadowOfRoomId) : null;
                        const hasShadow = !isShadow && rooms.some(r => r.shadowOfRoomId === room.id);
                        const bedroomsInProperty = rooms.filter(r => (r.roomType || "bedroom") === "bedroom" && isPrimaryRoom(r));
                        const bathroomsInProperty = rooms.filter(r => r.roomType === "bathroom" && isPrimaryRoom(r));
                        const isBathroom = room.roomType === "bathroom";
                        const isBedroomRoom = (room.roomType || "bedroom") === "bedroom";
                        const linkedBedroomIds: number[] = (room.associatedBedroomIds as number[] | null) || [];
                        const servingBathrooms = isBedroomRoom
                          ? bathroomsInProperty.filter(b => ((b.associatedBedroomIds as number[] | null) || []).includes(room.id))
                          : [];
                        return (
                        <TableRow key={room.id} data-testid={`row-room-${room.id}`} className={isShadow ? "bg-yellow-50/40" : ""}>
                          <TableCell className="font-medium" data-testid={`text-room-number-${room.id}`}>
                            <div className={`flex items-center gap-1.5 ${isShadow ? "pl-4" : ""}`}>
                              {isShadow && <Copy className="h-3 w-3 text-yellow-600 shrink-0" />}
                              {(room.roomType === "common" || room.roomType === "common_area" || room.roomType === "common_space" || room.roomType === "exterior") && !isShadow ? (
                                <Input
                                  defaultValue={room.roomNumber || ""}
                                  onBlur={(e) => {
                                    const trimmed = e.target.value.trim();
                                    if (trimmed && trimmed !== (room.roomNumber || "")) {
                                      updateRoomMutation.mutate({ roomId: room.id, data: { roomNumber: trimmed } as Partial<Room> });
                                    }
                                  }}
                                  placeholder="Label..."
                                  className="text-sm h-8 w-20"
                                  data-testid={`input-room-number-${room.id}`}
                                />
                              ) : (
                                room.roomNumber
                              )}
                              {isShadow && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800" data-testid={`badge-shadow-${room.id}`}>
                                  Shadow of #{parentRoom?.roomNumber ?? room.shadowOfRoomId}
                                </span>
                              )}
                              {hasShadow && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300" data-testid={`badge-has-shadow-${room.id}`}>
                                  Has shadow
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Select
                              value={room.roomType || "bedroom"}
                              disabled={readOnly || isPadsplitManagedRoom(room)}
                              onValueChange={(val) => {
                                updateRoomMutation.mutate({ roomId: room.id, data: { roomType: val } as Partial<Room> });
                              }}
                            >
                              <SelectTrigger className="h-8 text-xs w-24" title={isPadsplitManagedRoom(room) ? PADSPLIT_ROOM_MANAGED_MESSAGE : room.roomType || "bedroom"} data-testid={`select-room-type-${room.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="bedroom">Bedroom</SelectItem>
                                <SelectItem value="bathroom">Bathroom</SelectItem>
                                <SelectItem value="common_space">Common Space</SelectItem>
                                <SelectItem value="exterior">Exterior</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell data-testid={`cell-room-member-${room.id}`}>
                            {(() => {
                              const member = memberByRoomId.get(room.id);
                              if (!member) {
                                if (isBedroomRoom && !isShadow) {
                                  return (
                                    <span
                                      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200"
                                      data-testid={`badge-room-vacant-${room.id}`}
                                    >
                                      <span className="inline-block w-2 h-2 rounded-full bg-amber-400" />
                                      {(propertyMembersQuery.data ?? []).some(m => m.roomId === room.id) ? "Residency needs review" : "No verified resident"}
                                    </span>
                                  );
                                }
                                return <span className="text-gray-400">&mdash;</span>;
                              }
                              const label = member.displayName || member.name;
                              return (
                                <Link href={`/app/members/${member.id}`} data-testid={`link-room-member-${room.id}`} className="block truncate text-brand hover:underline" title={label}>
                                  {label}
                                  <span className="block text-xs text-gray-500">
                                    Stored: {member.status ?? "Unknown"} · {member.residency?.residencyEvidence === "local_current" ? "Local tenancy verified" : "Live presence verified"}
                                  </span>
                                </Link>
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            {isBathroom ? (
                              <div className="flex flex-col gap-1 min-w-[140px]" data-testid={`cell-bedroom-links-${room.id}`}>
                                <div className="flex items-center gap-1 flex-wrap">
                                  {linkedBedroomIds.length > 0 && (
                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold ${
                                      linkedBedroomIds.length === 1 ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"
                                    }`} data-testid={`badge-bath-assoc-type-${room.id}`}>
                                      {linkedBedroomIds.length === 1 ? "Private" : `Shared (${linkedBedroomIds.length})`}
                                    </span>
                                  )}
                                  {linkedBedroomIds.length > 4 && (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span
                                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-300 cursor-help"
                                            data-testid={`warning-bathroom-overcapacity-${room.id}`}
                                          >
                                            <AlertTriangle className="h-3 w-3" />
                                            {linkedBedroomIds.length} bedrooms
                                          </span>
                                        </TooltipTrigger>
                                        <TooltipContent side="top" className="max-w-xs">
                                          <p className="text-xs">
                                            PadSplit compliance: a bathroom may serve a maximum of 4 bedrooms (4 people).
                                            This bathroom is linked to {linkedBedroomIds.length} bedrooms.
                                          </p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                </div>
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button variant="outline" size="sm" className="h-7 text-xs px-2 w-full justify-start truncate" data-testid={`button-assign-bedrooms-${room.id}`}>
                                      {linkedBedroomIds.length === 0
                                        ? <span className="text-gray-400">Assign bedrooms…</span>
                                        : <span className="truncate">{linkedBedroomIds.map(bid => bedroomsInProperty.find(b => b.id === bid)?.roomNumber ?? `#${bid}`).join(", ")}</span>
                                      }
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-52 p-2" align="start">
                                    <p className="text-xs font-semibold text-gray-700 mb-2">Associate bedrooms</p>
                                    {bedroomsInProperty.length === 0 ? (
                                      <p className="text-xs text-gray-400">No bedrooms in this property</p>
                                    ) : (
                                      <div className="space-y-1">
                                        {bedroomsInProperty.map(br => {
                                          const checked = linkedBedroomIds.includes(br.id);
                                          const newIds = checked
                                            ? linkedBedroomIds.filter(i => i !== br.id)
                                            : [...linkedBedroomIds, br.id];
                                          return (
                                            <button
                                              key={br.id}
                                              type="button"
                                              className={`w-full text-left flex items-center gap-2 px-2 py-1 rounded text-xs transition-colors ${checked ? "bg-blue-50 dark:bg-blue-950/40 text-blue-800 dark:text-blue-200 font-medium" : "hover:bg-gray-50 text-gray-700"}`}
                                              onClick={() => updateRoomMutation.mutate({ roomId: room.id, data: { associatedBedroomIds: newIds } as Partial<Room> })}
                                              data-testid={`assign-bedroom-${room.id}-${br.id}`}
                                            >
                                              <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${checked ? "bg-blue-600 border-blue-600" : "border-gray-300"}`}>
                                                {checked && <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 12 12"><path d="M10 3L5 8.5 2 5.5" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                              </span>
                                              {br.roomNumber}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </PopoverContent>
                                </Popover>
                              </div>
                            ) : isBedroomRoom ? (
                              <div className="min-w-[100px]" data-testid={`cell-serving-baths-${room.id}`}>
                                {servingBathrooms.length === 0 ? (
                                  <span className="text-xs text-gray-400">—</span>
                                ) : (
                                  <div className="flex flex-wrap gap-1">
                                    {servingBathrooms.map(b => (
                                      <span key={b.id} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-purple-50 text-purple-800" data-testid={`badge-served-by-${room.id}-${b.id}`}>
                                        {b.roomNumber}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400" data-testid={`cell-no-links-${room.id}`}>—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              room.status === "occupied" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"
                            }`} data-testid={`text-room-status-${room.id}`}>
                              {room.status}
                            </span>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const title = room.roomTitle;
                              if (!title) {
                                return (
                                  <span
                                    className="text-sm text-muted-foreground"
                                    data-testid={`text-room-title-${room.id}`}
                                  >
                                    —
                                  </span>
                                );
                              }
                              const LIMIT = 100;
                              const isLong = title.length > LIMIT;
                              const isExpanded = expandedRoomTitles.has(room.id);
                              const displayed = isLong && !isExpanded ? title.slice(0, LIMIT) : title;
                              return (
                                <span className="inline-block w-80 whitespace-normal break-words text-sm text-muted-foreground" title={title}>
                                  <span data-testid={`text-room-title-${room.id}`}>{displayed}</span>
                                  {isLong && !isExpanded && <span className="text-muted-foreground">…</span>}
                                  {isLong && (
                                    <button
                                      type="button"
                                      className="ml-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                                      data-testid={`button-toggle-room-title-${room.id}`}
                                      onClick={() => setExpandedRoomTitles(prev => {
                                        const next = new Set(prev);
                                        if (next.has(room.id)) next.delete(room.id); else next.add(room.id);
                                        return next;
                                      })}
                                    >
                                      {isExpanded ? "−" : "+"}
                                    </button>
                                  )}
                                </span>
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            {isPadsplitManagedRoom(room) ? (
                              <p className="text-sm whitespace-pre-wrap break-words" title={PADSPLIT_ROOM_MANAGED_MESSAGE} data-testid={`text-room-description-${room.id}`}>{room.roomDescription || "—"}</p>
                            ) : <Input
                              defaultValue={room.roomDescription || ""}
                              onBlur={(e) => {
                                if (e.target.value !== (room.roomDescription || "")) {
                                  updateRoomMutation.mutate({ roomId: room.id, data: { roomDescription: e.target.value } as Partial<Room> });
                                }
                              }}
                              placeholder="Description..."
                              className="text-sm h-8 w-40"
                              disabled={readOnly}
                              data-testid={`input-room-description-${room.id}`}
                            />}
                          </TableCell>
                          <TableCell>
                            {(room.inputImageUrl || room.outputImageUrl || room.hasSavedImages) ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                aria-label={`View images for ${room.roomNumber}`}
                                title="View in Image Generation"
                                onClick={() => setRoomsView("images")}
                                data-testid={`button-room-images-${room.id}`}
                              >
                                <SquareCheck aria-hidden="true" className="text-brand h-5 w-5" />
                              </Button>
                            ) : <span className="text-xs text-gray-400" data-testid={`text-room-no-images-${room.id}`}>—</span>}
                          </TableCell>
                          <TableCell>
                            {(property.managementType || "padsplit") === "self_managed" && !isPadsplitManagedRoom(room) ? (
                              <span className="text-xs text-gray-400" data-testid={`text-room-padsplit-id-na-${room.id}`}>N/A</span>
                            ) : isPadsplitManagedRoom(room) || (room.roomType || "bedroom") === "bedroom" ? (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Input
                                      value={room.padsplitRoomId || ""}
                                      readOnly
                                      className="text-sm h-8 w-20 px-1 bg-muted text-muted-foreground"
                                      title={room.padsplitRoomId || "Not yet imported"}
                                      placeholder="DB only"
                                      data-testid={`input-room-padsplit-id-${room.id}`}
                                    />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {room.padsplitRoomId
                                      ? "PadSplit Room ID is locked once set. Contact support if it needs to change."
                                      : "PadSplit Room ID is set directly in the database by the harvest process."}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              <span className="text-xs text-gray-400">N/A</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <RoomDimensionCell
                              room={room}
                              readOnly={readOnly}
                              onSave={(data) => updateRoomMutation.mutate({ roomId: room.id, data: data as Partial<Room> })}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              defaultValue={room.notes || ""}
                              onBlur={(e) => {
                                if (e.target.value !== (room.notes || "")) {
                                  updateRoomMutation.mutate({ roomId: room.id, data: { notes: e.target.value } as Partial<Room> });
                                }
                              }}
                              placeholder="Add notes..."
                              className="text-sm h-8 w-full"
                              data-testid={`input-room-notes-${room.id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {(room.roomType || "bedroom") === "bedroom" && !isShadow && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  title="Link as shadow of another room"
                                  onClick={() => {
                                    setLinkShadowRoomId(room.id);
                                    setLinkShadowTargetId("");
                                    setLinkShadowOpen(true);
                                  }}
                                  data-testid={`button-link-shadow-${room.id}`}
                                >
                                  <Link2 className="h-4 w-4 text-gray-400 hover:text-yellow-600" />
                                </Button>
                              )}
                              {isShadow && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  title="Unlink shadow"
                                  onClick={() => updateRoomMutation.mutate({ roomId: room.id, data: { shadowOfRoomId: null } as Partial<Room> })}
                                  data-testid={`button-unlink-shadow-${room.id}`}
                                >
                                  <Link2Off className="h-4 w-4 text-yellow-600 hover:text-gray-500" />
                                </Button>
                              )}
                              {!readOnly && !isPadsplitManagedRoom(room) && !rooms.some(child => child.shadowOfRoomId === room.id && isPadsplitManagedRoom(child)) && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => deleteRoomMutation.mutate(room.id)}
                                  data-testid={`button-delete-room-${room.id}`}
                                >
                                  <Trash2 className="h-4 w-4 text-gray-400 hover:text-red-500" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}

              <Dialog open={linkShadowOpen} onOpenChange={setLinkShadowOpen}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Link Room as Shadow</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-2">
                    <p className="text-sm text-gray-600">
                      Select the parent room that this room is a shadow of (i.e., they share the same physical space).
                    </p>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Parent Room</label>
                      <Select value={linkShadowTargetId} onValueChange={setLinkShadowTargetId}>
                        <SelectTrigger data-testid="select-shadow-target">
                          <SelectValue placeholder="Select a room..." />
                        </SelectTrigger>
                        <SelectContent>
                          {rooms
                            .filter(r => r.id !== linkShadowRoomId && !r.shadowOfRoomId && (r.roomType || "bedroom") === "bedroom")
                            .map(r => (
                              <SelectItem key={r.id} value={String(r.id)}>{r.roomNumber}</SelectItem>
                            ))
                          }
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setLinkShadowOpen(false)} data-testid="button-cancel-link-shadow">
                        Cancel
                      </Button>
                      <Button
                        className="bg-brand"
                        disabled={!linkShadowTargetId || updateRoomMutation.isPending}
                        onClick={() => {
                          if (linkShadowRoomId && linkShadowTargetId) {
                            updateRoomMutation.mutate(
                              { roomId: linkShadowRoomId, data: { shadowOfRoomId: parseInt(linkShadowTargetId) } as Partial<Room> },
                              { onSuccess: () => setLinkShadowOpen(false) }
                            );
                          }
                        }}
                        data-testid="button-confirm-link-shadow"
                      >
                        {updateRoomMutation.isPending ? "Linking..." : "Link as Shadow"}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            </fieldset>
          </TabsContent>

          <TabsContent value="documents">
            <fieldset disabled={readOnly} style={{ display: "contents" }}>
            <div className="border rounded-lg p-6" data-tour="property-detail-documents">
              <div className="flex items-center gap-2 mb-6">
                <FileText className="h-5 w-5 text-brand" />
                <h3 className="text-lg font-semibold text-gray-900" data-testid="text-documents-heading">Documents</h3>
                <span className="text-xs text-gray-500 ml-1">All invoices, receipts, and bids uploaded for this property</span>
              </div>

              {documentsQuery.isLoading ? (
                <div className="space-y-2" data-testid="documents-loading">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : (() => {
                const data = documentsQuery.data;
                const filtersActive = docKindFilter !== "all" || docVendorFilter !== "all" || docNameQuery !== "" || docMinAmount !== "" || docMaxAmount !== "";
                const sorted = data?.items ?? [];
                const total = data?.total ?? 0;
                const vendorOptions = data?.vendorOptions ?? [];
                const totalPages = Math.max(1, Math.ceil(total / docPageSize));
                if (total === 0 && !filtersActive) {
                  return (
                    <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-lg" data-testid="documents-empty-state">
                      <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                      <h4 className="text-base font-medium text-gray-600 mb-1">No documents yet</h4>
                      <p className="text-sm text-gray-400">Invoices, receipts, and bids uploaded from the Financials tab will appear here.</p>
                    </div>
                  );
                }

                const SortIcon = ({ field }: { field: "uploadedAt" | "vendor" | "amount" }) => {
                  if (docSortField !== field) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
                  return docSortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
                };

                return (
                  <>
                    <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="documents-filters">
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-gray-700">Kind</label>
                        <Select
                          value={docKindFilter}
                          onValueChange={(v) => updateDocsQuery({ kind: v === "all" ? null : v })}
                        >
                          <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="select-document-kind-filter">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All kinds</SelectItem>
                            {Object.entries(DOCUMENT_CATEGORY_LABELS).map(([key, label]) => (
                              <SelectItem key={key} value={key}>{label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-gray-700">Vendor</label>
                        <Select
                          value={docVendorFilter}
                          onValueChange={(v) => updateDocsQuery({ vendor: v === "all" ? null : v })}
                        >
                          <SelectTrigger className="w-[220px] h-9 text-sm" data-testid="select-document-vendor-filter">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All vendors</SelectItem>
                            <SelectItem value="__none__">No vendor</SelectItem>
                            {vendorOptions.map((v) => (
                              <SelectItem key={v} value={v}>{v}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-gray-700">Search file name</label>
                        <Input
                          type="text"
                          value={docNameQuery}
                          onChange={(e) => updateDocsQuery({ q: e.target.value || null })}
                          placeholder="e.g. roof"
                          className="w-[200px] h-9 text-sm"
                          data-testid="input-document-search"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-gray-700">Min amount</label>
                        <Input
                          type="number"
                          inputMode="decimal"
                          value={docMinAmount}
                          onChange={(e) => updateDocsQuery({ min: e.target.value || null })}
                          placeholder="$0"
                          className="w-[110px] h-9 text-sm"
                          data-testid="input-document-min-amount"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-gray-700">Max amount</label>
                        <Input
                          type="number"
                          inputMode="decimal"
                          value={docMaxAmount}
                          onChange={(e) => updateDocsQuery({ max: e.target.value || null })}
                          placeholder="$10,000"
                          className="w-[110px] h-9 text-sm"
                          data-testid="input-document-max-amount"
                        />
                      </div>
                      {filtersActive && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => updateDocsQuery({ kind: null, vendor: null, q: null, min: null, max: null })}
                          data-testid="button-clear-document-filters"
                          className="h-9"
                        >
                          <X className="h-4 w-4 mr-1" /> Clear filters
                        </Button>
                      )}
                      <div className="flex flex-col gap-1 ml-auto">
                        <label className="text-xs font-semibold text-gray-700">Per page</label>
                        <Select
                          value={String(docPageSize)}
                          onValueChange={(v) => {
                            const n = parseInt(v);
                            if (isValidDocPageSize(n)) persistDocPageSize(n);
                            updateDocsQuery({ pageSize: v === "50" ? null : v });
                          }}
                        >
                          <SelectTrigger className="w-[90px] h-9 text-sm" data-testid="select-documents-page-size">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DOC_PAGE_SIZE_OPTIONS.map((n) => (
                              <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="text-xs text-gray-500 self-center" data-testid="text-documents-count">
                        {total === 0
                          ? "0 results"
                          : `Showing ${(docPage - 1) * docPageSize + 1}–${(docPage - 1) * docPageSize + sorted.length} of ${total}`}
                      </div>
                    </div>
                {sorted.length === 0 ? (
                  <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg" data-testid="documents-no-results">
                    <FileText className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No documents match the current filters.</p>
                  </div>
                ) : (
                <div className="border rounded-md overflow-hidden">
                  <Table data-testid="table-documents">
                    <TableHeader>
                      <TableRow>
                        <TableHead>File</TableHead>
                        <TableHead>Kind</TableHead>
                        <TableHead>
                          <button
                            type="button"
                            onClick={() => handleDocSort("uploadedAt")}
                            className="inline-flex items-center gap-1 font-medium hover:text-gray-900"
                            data-testid="button-sort-uploaded"
                          >
                            Uploaded <SortIcon field="uploadedAt" />
                          </button>
                        </TableHead>
                        <TableHead>
                          <button
                            type="button"
                            onClick={() => handleDocSort("vendor")}
                            className="inline-flex items-center gap-1 font-medium hover:text-gray-900"
                            data-testid="button-sort-vendor"
                          >
                            Vendor <SortIcon field="vendor" />
                          </button>
                        </TableHead>
                        <TableHead className="text-right">
                          <button
                            type="button"
                            onClick={() => handleDocSort("amount")}
                            className="inline-flex items-center gap-1 font-medium hover:text-gray-900 ml-auto"
                            data-testid="button-sort-amount"
                          >
                            Amount <SortIcon field="amount" />
                          </button>
                        </TableHead>
                        <TableHead className="w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sorted.map((doc) => {
                        const extracted = (doc.extractedData as Record<string, unknown>) || {};
                        const amountRaw = extracted.totalAmount;
                        const amountNum = typeof amountRaw === "number" ? amountRaw : (amountRaw != null ? parseFloat(String(amountRaw)) : NaN);
                        const amountText = Number.isFinite(amountNum) ? `$${amountNum.toFixed(2)}` : "—";
                        const categoryLabel = DOCUMENT_CATEGORY_LABELS[doc.category as DocumentCategory] || doc.category;
                        const canDelete = doc.canDelete ?? (currentUserId != null && doc.userId === currentUserId);
                        let uploadedText = "—";
                        try {
                          uploadedText = format(new Date(doc.uploadedAt as unknown as string), "MMM d, yyyy");
                        } catch { /* ignore */ }
                        return (
                          <TableRow key={doc.id} data-testid={`row-document-${doc.id}`}>
                            <TableCell className="max-w-xs">
                              <a
                                href={`/api/app/property-documents/${doc.id}/file`}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:underline truncate"
                                data-testid={`link-document-${doc.id}`}
                                title={doc.fileName}
                              >
                                <FileText className="h-4 w-4 shrink-0" />
                                <span className="truncate">{doc.fileName}</span>
                                <ExternalLink className="h-3 w-3 shrink-0 opacity-60" />
                              </a>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-gray-700" data-testid={`text-document-kind-${doc.id}`}>{categoryLabel}</span>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-gray-700" data-testid={`text-document-uploaded-${doc.id}`}>{uploadedText}</span>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-gray-700" data-testid={`text-document-vendor-${doc.id}`}>{doc.vendorName || "—"}</span>
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="text-sm font-medium text-gray-900" data-testid={`text-document-amount-${doc.id}`}>{amountText}</span>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setManageDocId(doc.id)}
                                  data-testid={`button-manage-document-${doc.id}`}
                                  className="text-gray-500 hover:text-orange-600 hover:bg-orange-50"
                                  title="Manage source document (re-extract, void or restore)"
                                >
                                  <FileSearch className="h-4 w-4" />
                                </Button>
                                {canDelete ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      if (readOnly) return;
                                      setDocToDelete({ id: doc.id, fileName: doc.fileName });
                                    }}
                                    disabled={readOnly || deleteDocumentMutation.isPending}
                                    data-testid={`button-delete-document-${doc.id}`}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    title="Delete document"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                ) : null}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                )}
                {totalPages > 1 && (
                  <div className="flex flex-wrap items-center justify-between gap-3 mt-4" data-testid="documents-pagination">
                    <div className="text-xs text-gray-500" data-testid="text-documents-page-info">
                      Page {docPage} of {totalPages}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={docPage <= 1}
                        onClick={() => updateDocsQuery({ page: null }, { keepPage: true })}
                        data-testid="button-documents-first-page"
                      >
                        First
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={docPage <= 1}
                        onClick={() => updateDocsQuery({ page: String(docPage - 1) }, { keepPage: true })}
                        data-testid="button-documents-prev-page"
                      >
                        Previous
                      </Button>
                      {(() => {
                        const windowSize = 5;
                        let start = Math.max(1, docPage - Math.floor(windowSize / 2));
                        let end = Math.min(totalPages, start + windowSize - 1);
                        start = Math.max(1, end - windowSize + 1);
                        const buttons = [];
                        for (let p = start; p <= end; p++) {
                          buttons.push(
                            <Button
                              key={p}
                              type="button"
                              variant={p === docPage ? "default" : "outline"}
                              size="sm"
                              onClick={() => updateDocsQuery({ page: p === 1 ? null : String(p) }, { keepPage: true })}
                              data-testid={`button-documents-page-${p}`}
                              className="min-w-[36px] px-2"
                            >
                              {p}
                            </Button>
                          );
                        }
                        return buttons;
                      })()}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={docPage >= totalPages}
                        onClick={() => updateDocsQuery({ page: String(docPage + 1) }, { keepPage: true })}
                        data-testid="button-documents-next-page"
                      >
                        Next
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={docPage >= totalPages}
                        onClick={() => updateDocsQuery({ page: String(totalPages) }, { keepPage: true })}
                        data-testid="button-documents-last-page"
                      >
                        Last
                      </Button>
                      <div className="flex items-center gap-1 ml-2">
                        <span className="text-xs text-gray-500">Go to</span>
                        <Input
                          type="number"
                          min={1}
                          max={totalPages}
                          defaultValue={docPage}
                          key={`page-jump-${docPage}-${totalPages}`}
                          className="w-[70px] h-8 text-sm"
                          data-testid="input-documents-page-jump"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              const val = parseInt((e.target as HTMLInputElement).value);
                              if (Number.isFinite(val)) {
                                const target = Math.min(Math.max(1, val), totalPages);
                                updateDocsQuery({ page: target === 1 ? null : String(target) }, { keepPage: true });
                              }
                            }
                          }}
                          onBlur={(e) => {
                            const val = parseInt(e.target.value);
                            if (Number.isFinite(val)) {
                              const target = Math.min(Math.max(1, val), totalPages);
                              if (target !== docPage) {
                                updateDocsQuery({ page: target === 1 ? null : String(target) }, { keepPage: true });
                              }
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
                  </>
                );
              })()}
            </div>
            </fieldset>
          </TabsContent>

          <TabsContent value="devices">
            <fieldset disabled={readOnly} style={{ display: "contents" }}>
            <div className="border rounded-lg p-4 mb-4 bg-white" data-testid="panel-thermostat-alert-settings" data-tour="property-detail-devices">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <label className="block text-sm font-semibold text-gray-700 mb-0.5">Out-of-range alert threshold</label>
                  <p className="text-xs text-gray-500 max-w-md">
                    Notify when an indoor temperature drifts more than this many °F past the active heating
                    or cooling setpoint. Lower it for high-end units, raise it for poorly insulated ones.
                    Default is 2°F.
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Input
                    type="number"
                    min={1}
                    max={10}
                    step={1}
                    className="w-20 text-sm"
                    defaultValue={property.thermostatHealthThresholdF ?? 2}
                    disabled={readOnly}
                    data-testid="input-thermostat-health-threshold"
                    onBlur={(e) => {
                      const raw = parseInt(e.target.value, 10);
                      const clamped = Math.max(1, Math.min(10, Number.isFinite(raw) ? raw : 2));
                      e.target.value = String(clamped);
                      if (clamped !== (property.thermostatHealthThresholdF ?? 2)) {
                        updateMutation.mutate({ thermostatHealthThresholdF: clamped } as Partial<Property>);
                      }
                    }}
                  />
                  <span className="text-sm text-gray-600">°F</span>
                </div>
              </div>
            </div>
            <PropertyDevicesPanel propertyId={propertyId!} highlightDeviceId={searchParams.get("deviceId") ?? undefined} />
            </fieldset>
          </TabsContent>

          <TabsContent value="appliances">
            <fieldset disabled={readOnly} style={{ display: "contents" }}>
            <div data-tour="property-detail-appliances">
            <AppliancesPanel
              propertyId={propertyId!}
              appliances={appliancesQuery.data ?? []}
              isLoading={appliancesQuery.isLoading}
              readOnly={readOnly}
              applianceFormOpen={applianceFormOpen}
              setApplianceFormOpen={setApplianceFormOpen}
              applianceToEdit={applianceToEdit}
              setApplianceToEdit={setApplianceToEdit}
              applianceToDelete={applianceToDelete}
              setApplianceToDelete={setApplianceToDelete}
              applianceDetailId={applianceDetailId}
              setApplianceDetailId={setApplianceDetailId}
              onAdd={(data) => createApplianceMutation.mutate(data)}
              onUpdate={(id, data) => updateApplianceMutation.mutate({ id, data })}
              onDelete={(id) => deleteApplianceMutation.mutate(id)}
              isSaving={createApplianceMutation.isPending || updateApplianceMutation.isPending}
              isDeleting={deleteApplianceMutation.isPending}
            />
            </div>
            </fieldset>
          </TabsContent>

          <TabsContent value="knowledge-base">
            <fieldset disabled={readOnly} style={{ display: "contents" }}>
            <div className="border rounded-lg p-6" data-tour="property-detail-kb">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-brand" />
                  <h3 className="text-lg font-semibold text-gray-900" data-testid="text-kb-heading">Knowledge Base</h3>
                  <span className="text-xs text-gray-500 ml-1">Reference info for this property</span>
                </div>
                <div className="flex items-center gap-2">
                  {!kbEditMode ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const currentContent = knowledgeBaseQuery.data?.content || DEFAULT_KB_TEMPLATE;
                          setKbDraft(currentContent);
                          setKbEditMode(true);
                        }}
                        data-testid="button-kb-edit"
                      >
                        <Edit2 className="h-4 w-4 mr-1" /> Edit
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setKbEditMode(false);
                          setKbDraft(null);
                        }}
                        data-testid="button-kb-cancel"
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        className="bg-brand"
                        disabled={saveKbMutation.isPending}
                        onClick={() => {
                          if (kbDraft !== null) saveKbMutation.mutate(kbDraft);
                        }}
                        data-testid="button-kb-save"
                      >
                        <Save className="h-4 w-4 mr-1" />
                        {saveKbMutation.isPending ? "Saving..." : "Save"}
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {knowledgeBaseQuery.isLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                </div>
              ) : kbEditMode ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="kb-editor-area">
                  <div className="flex flex-col">
                    <div className="flex items-center gap-1 mb-2">
                      <Edit2 className="h-3 w-3 text-gray-400" />
                      <span className="text-xs text-gray-500 font-medium">Editor</span>
                    </div>
                    <Textarea
                      value={kbDraft ?? ""}
                      onChange={(e) => setKbDraft(e.target.value)}
                      rows={30}
                      className="font-mono text-sm resize-none flex-1 min-h-[500px]"
                      placeholder="Write your knowledge base in Markdown..."
                      data-testid="textarea-kb-editor"
                    />
                  </div>
                  <div className="flex flex-col">
                    <div className="flex items-center gap-1 mb-2">
                      <Eye className="h-3 w-3 text-gray-400" />
                      <span className="text-xs text-gray-500 font-medium">Preview</span>
                    </div>
                    <div className="border rounded-md p-4 bg-gray-50 overflow-auto min-h-[500px] prose prose-sm max-w-none" data-testid="kb-preview-area">
                      <ReactMarkdown>{kbDraft ?? ""}</ReactMarkdown>
                    </div>
                  </div>
                </div>
              ) : (
                <div data-testid="kb-content-area">
                  {knowledgeBaseQuery.data?.content ? (
                    <div className="prose prose-sm max-w-none border rounded-md p-6 bg-gray-50 min-h-[300px]" data-testid="kb-rendered-content">
                      <ReactMarkdown>{knowledgeBaseQuery.data.content}</ReactMarkdown>
                    </div>
                  ) : (
                    <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-lg" data-testid="kb-empty-state">
                      <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                      <h4 className="text-base font-medium text-gray-600 mb-1">No knowledge base yet</h4>
                      <p className="text-sm text-gray-400 mb-4">
                        Start with the template to build your property knowledge base.
                      </p>
                      <div className="flex gap-2 justify-center">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setKbDraft(DEFAULT_KB_TEMPLATE);
                            setKbEditMode(true);
                          }}
                          data-testid="button-kb-use-template"
                        >
                          <Edit2 className="h-4 w-4 mr-1" /> Use Template
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <AIMemorySection
                endpoint={`/api/app/properties/${propertyId}/ai-memory`}
                queryKey={["/api/app/properties", propertyId, "ai-memory"]}
                canEdit={canEdit("properties")}
                description="PadPulse remembers facts about this property here (e.g. door codes, recurring vendor notes). You can edit them anytime."
                testIdPrefix="property-ai-memory"
                aiDraftingAllowed={aiDraftingAllowed}
              />
              <AiMemoryEntriesPanel
                listQueryKey={["/api/app/properties", propertyId, "ai-memory-entries"]}
                listEndpoint={`/api/app/properties/${propertyId}/ai-memory-entries`}
                canEdit={canEdit("properties")}
                testIdPrefix="property-ai-memory-entries"
                aiDraftingAllowed={aiDraftingAllowed}
              />
            </div>
            </fieldset>
          </TabsContent>

          <TabsContent value="maintenance">
            <fieldset disabled={readOnly} style={{ display: "contents" }}>
            <div data-tour="property-detail-maintenance">
              <PropertyMaintenanceTasksPanel propertyId={propertyId!} />
            </div>
            </fieldset>
          </TabsContent>

          {promptSettingsVisible ? (
            <TabsContent value="prompts-settings">
              <InlinePromptSettingsTabs />
            </TabsContent>
          ) : null}

        </Tabs>
      </div>
      {manageDocId !== null && (
        <ManageDocumentDialog
          open={manageDocId !== null}
          onClose={() => {
            setManageDocId(null);
            queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "documents"] });
          }}
          documentId={manageDocId}
        />
      )}
      <AlertDialog open={!!docToDelete} onOpenChange={(v) => !v && setDocToDelete(null)}>
        <AlertDialogContent data-testid="dialog-delete-document-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete document?</AlertDialogTitle>
            <AlertDialogDescription>
              {docToDelete ? `Delete "${docToDelete.fileName}"? This cannot be undone.` : "This cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-document">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-confirm-delete-document"
              disabled={deleteDocumentMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (!docToDelete) return;
                const id = docToDelete.id;
                deleteDocumentMutation.mutate(id, {
                  onSettled: () => setDocToDelete(null),
                });
              }}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleteDocumentMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={archiveCardModalOpen}
        onOpenChange={(v) => {
          if (!v) {
            setArchiveCardModalOpen(false);
            setMercuryRepromptMode(false);
            // Revert the dropdown back to the property's current status
            // since the host backed out of the archive flow. (In reprompt mode
            // the property is already terminal, so this is a no-op revert.)
            setPropertyStatus(property?.status ?? "active");
          }
        }}
      >
        <AlertDialogContent data-testid="dialog-archive-mercury-cards">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Mercury cards for this property?</AlertDialogTitle>
            <AlertDialogDescription>
              This property is{" "}
              {pendingTerminalStatus === "sold"
                ? "sold"
                : pendingTerminalStatus === "disposed"
                  ? "disposed"
                  : "archived"}{" "}
              and still has {archiveLinkedCards.length} linked Mercury{" "}
              {archiveLinkedCards.length === 1 ? "card" : "cards"} active. {archiveLinkedCards.length === 1 ? "It stays" : "They stay"} linked and can keep incurring charges until you cancel {archiveLinkedCards.length === 1 ? "it" : "them"} in Mercury. What would you like to do?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="my-3 max-h-56 overflow-y-auto rounded-md border bg-gray-50 p-3 text-sm" data-testid="list-archive-mercury-cards">
            {archiveLinkedCards.map((c) => (
              <li key={c.id} className="flex items-center justify-between py-1" data-testid={`item-archive-mercury-card-${c.id}`}>
                <span className="truncate">{c.nickname || "Unnamed card"}</span>
                <span className="ml-2 text-xs text-gray-500">
                  {(c.brand || "card")}{c.last4 ? ` •••• ${c.last4}` : ""}
                </span>
              </li>
            ))}
          </ul>
          <AlertDialogFooter className="gap-2 sm:flex-row sm:justify-end">
            <AlertDialogCancel data-testid="button-archive-mercury-back">Back</AlertDialogCancel>
            <Button
              variant="outline"
              data-testid="button-archive-mercury-remind"
              onClick={() => submitArchiveWithMercuryAction("remind_30d")}
            >
              Remind me in 30 days
            </Button>
            <Button
              variant="outline"
              data-testid="button-archive-mercury-not-now"
              onClick={() => submitArchiveWithMercuryAction("not_now")}
            >
              Not now
            </Button>
            <AlertDialogAction
              data-testid="button-archive-mercury-cancel"
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              onClick={(e) => {
                e.preventDefault();
                submitArchiveWithMercuryAction("cancel");
              }}
            >
              Yes, cancel cards
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog
        open={saleModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setSaleModalOpen(false);
            setPendingSaleStatus(null);
            // Revert the dropdown back to the property's current status
            // since the host backed out of the sale flow.
            setPropertyStatus(property?.status ?? "active");
          }
        }}
      >
        <DialogContent data-testid="dialog-sale-details">
          <DialogHeader>
            <DialogTitle>
              {pendingSaleStatus === "disposed" ? "Record disposition details" : "Record sale details"}
            </DialogTitle>
            <DialogDescription>
              Optionally capture the {pendingSaleStatus === "disposed" ? "disposition" : "sale"} date and amount for this property. You can leave these blank and add them later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <DatePickerField
              label={pendingSaleStatus === "disposed" ? "Disposition Date" : "Sale Date"}
              value={saleDateInput}
              onChange={(val) => setSaleDateInput(val)}
              testId="input-sale-date"
            />
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                {pendingSaleStatus === "disposed" ? "Disposition Amount" : "Sale Price"}
              </label>
              <Input
                value={salePriceInput}
                onChange={(e) => setSalePriceInput(e.target.value)}
                placeholder="$0"
                className="text-sm"
                data-testid="input-sale-price"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setSaleModalOpen(false);
                setPendingSaleStatus(null);
                setPropertyStatus(property?.status ?? "active");
              }}
              data-testid="button-sale-cancel"
            >
              Cancel
            </Button>
            <Button onClick={submitSaleDetails} data-testid="button-sale-save">
              {pendingSaleStatus === "disposed" ? "Mark Disposed" : "Mark Sold"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

interface PropertyThermostat {
  deviceId: string;
  seamName: string;
  customName: string | null;
  online: boolean;
  temperatureF: number | null;
  relativeHumidity: number | null;
  heatSetPointF: number | null;
  coolSetPointF: number | null;
  hvacMode: string | null;
  location: string | null;
}
interface PropertyLock {
  deviceId: string;
  displayName: string | null;
  online: boolean;
  locked: boolean | null;
  batteryPct: number | null;
  location: string | null;
  roomId: number | null;
  pinSource: "property" | "room" | null;
  provider?: "seam" | "ttlock";
  // Task #1803 — TTLock quiet-lock tier: when true, the server skipped
  // the live queryStatus call because the lock has had no unlock event
  // in 7+ days. UI shows a "Last seen ..." hint instead of "Unknown".
  liveStatusSkipped?: boolean;
  lastUnlockAt?: string | null;
}
// A lock the operator owns (via a connected Seam account) that the
// fuzzy match did NOT attach to this property. Surfacing these lets
// operators rescue a previously-mismatched lock without having to
// rename anything in Seam.
interface AssignableLock {
  deviceId: string;
  displayName: string | null;
  location: string | null;
  pinnedPropertyId: number | null;
  pinnedRoomId: number | null;
}
interface ThermostatReadingPoint {
  recordedAt: string;
  temperatureF: number | null;
  humidityPct: number | null;
  coolSetPointF: number | null;
  heatSetPointF: number | null;
}
interface ThermostatLatestSnapshot {
  recordedAt: string;
  temperatureF: number | null;
  humidityPct: number | null;
  coolSetPointF: number | null;
  heatSetPointF: number | null;
}

// Task #1201: range options shown above each thermostat chart so an
// operator can widen the window from the default 24h without leaving
// the property page. Backed by the existing endpoint's `hours`/`days`
// params; React Query dedupes shared keys when several cards sit on
// the default window.
const THERMOSTAT_RANGE_OPTIONS: Array<{ label: string; hours: number }> = [
  { label: "24h", hours: 24 },
  { label: "3d", hours: 72 },
  { label: "7d", hours: 168 },
];

export function PropertyDevicesPanel({ propertyId, highlightDeviceId }: { propertyId: number; highlightDeviceId?: string }) {
  const devicesQuery = useQuery<{
    thermostats: PropertyThermostat[];
    locks: PropertyLock[];
    assignableLocks?: AssignableLock[];
    ignoredLockCount?: number;
  }>({
    queryKey: ["/api/app/properties", propertyId, "devices"],
    queryFn: async () => {
      const res = await fetch(`/api/app/properties/${propertyId}/devices`, { credentials: "include" });
      if (!res.ok) throw responseError(res.status, "Failed to load property devices");
      return res.json();
    },
  });
  const roomsQuery = useQuery<Room[]>({
    queryKey: ["/api/app/properties", propertyId, "rooms"],
    queryFn: async () => {
      const res = await fetch(`/api/app/properties/${propertyId}/rooms`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load rooms");
      return res.json();
    },
  });
  // Task #977: pull notifications so each thermostat card can show the same
  // "Stuck for X" pill the dropdown does when there's an open
  // thermostat_health alert for that device.
  const notificationsQuery = useQuery<Array<{ type: string | null; link: string | null; read: boolean | null; createdAt: Date | string | null }>>({
    queryKey: ["/api/app/notifications"],
  });

  // Task #1551: flash the device card matching the deep-link deviceId so the
  // host immediately sees which device triggered the notification.
  const [flashingDeviceId, setFlashingDeviceId] = useState<string | null>(null);
  const handledHighlightRef = useRef<string | null>(null);
  useEffect(() => {
    if (!highlightDeviceId) return;
    if (handledHighlightRef.current === highlightDeviceId) return;
    if (!devicesQuery.data) return;
    const allDeviceIds = [
      ...(devicesQuery.data.thermostats?.map((t) => t.deviceId) ?? []),
      ...(devicesQuery.data.locks?.map((l) => l.deviceId) ?? []),
    ];
    if (!allDeviceIds.includes(highlightDeviceId)) {
      handledHighlightRef.current = highlightDeviceId;
      return;
    }
    handledHighlightRef.current = highlightDeviceId;
    setFlashingDeviceId(highlightDeviceId);
    const timer = window.setTimeout(() => setFlashingDeviceId(null), 2500);
    return () => window.clearTimeout(timer);
  }, [highlightDeviceId, devicesQuery.data]);

  const [, setNowTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  if (devicesQuery.isLoading) {
    return (
      <div className="border rounded-lg p-6 text-sm text-gray-500" data-testid="text-devices-loading">
        Loading devices…
      </div>
    );
  }
  if (devicesQuery.isError) {
    const restricted = (devicesQuery.error as { status?: number } | null)?.status === 403;
    return (
      <div className="border rounded-lg p-6 text-sm text-gray-500" role="alert" data-testid="text-devices-error">
        {restricted
          ? "You don’t have permission to view devices for this property."
          : "Unable to load devices for this property. Please try again."}
      </div>
    );
  }
  const rawData = devicesQuery.data;
  const data = {
    thermostats: rawData?.thermostats ?? [],
    locks: rawData?.locks ?? [],
    assignableLocks: rawData?.assignableLocks ?? [],
    ignoredLockCount: rawData?.ignoredLockCount ?? 0,
  };
  const assignableLocks = data.assignableLocks;
  const ignoredLockCount = data.ignoredLockCount;
  const hasAny = data.thermostats.length > 0 || data.locks.length > 0 || assignableLocks.length > 0;
  if (!hasAny) {
    return (
      <div className="border rounded-lg p-8 text-center" data-testid="empty-devices">
        <Thermometer className="h-10 w-10 text-gray-300 mx-auto mb-3" />
        <h3 className="text-base font-semibold text-gray-900 mb-1">No devices linked yet</h3>
        <p className="text-sm text-gray-500 mb-4">
          Link a thermostat to this property from the Thermostats page → Devices tab,
          or pair a smart lock to this property from the Smart Locks page → Devices tab.
        </p>
        {ignoredLockCount > 0 && (
          <div
            className="mx-auto max-w-md mb-4 border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-xs text-gray-600 flex items-center justify-between gap-3"
            data-testid="note-ignored-locks"
          >
            <span className="text-left">
              {ignoredLockCount} lock{ignoredLockCount === 1 ? "" : "s"} not shown because they are marked Ignored.
            </span>
            <Link
              href="/app/devices/smart-locks?tab=connected_accounts"
              className="text-brand hover:underline whitespace-nowrap"
              data-testid="link-manage-ignored-locks"
            >
              Manage
            </Link>
          </div>
        )}
        <Button asChild variant="outline" size="sm" data-testid="button-go-thermostats">
          <a href="/app/devices/thermostats">Open Thermostats</a>
        </Button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-4" data-testid="section-thermostats">
        <div className="flex items-center gap-2">
          <Thermometer className="h-5 w-5 text-brand" />
          <h3 className="text-lg font-semibold text-gray-900">Thermostats</h3>
          <span className="text-xs text-gray-500 ml-1">
            {data.thermostats.length} attached
          </span>
        </div>
        {data.thermostats.length === 0 ? (
          <div className="border rounded-lg p-6 text-sm text-gray-500" data-testid="text-no-thermostats">
            No thermostats linked to this property yet.
          </div>
        ) : (
          data.thermostats.map((t) => (
            <ThermostatCard
              key={t.deviceId}
              propertyId={propertyId}
              thermostat={t}
              notifications={notificationsQuery.data ?? []}
              isFlashing={flashingDeviceId === t.deviceId}
            />
          ))
        )}
      </div>
      <div className="space-y-4" data-testid="section-locks">
        <div className="flex items-center gap-2">
          <LockIcon className="h-5 w-5 text-brand" />
          <h3 className="text-lg font-semibold text-gray-900">Locks</h3>
          <span className="text-xs text-gray-500 ml-1">{data.locks.length} attached</span>
        </div>
        {(data.ignoredLockCount ?? 0) > 0 && (
          <div
            className="border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-xs text-gray-600 flex items-center justify-between gap-3"
            data-testid="note-ignored-locks"
          >
            <span>
              {data.ignoredLockCount} lock{(data.ignoredLockCount ?? 0) === 1 ? "" : "s"} not shown because they are marked Ignored.
            </span>
            <Link
              href="/app/devices/smart-locks?tab=connected_accounts"
              className="text-brand hover:underline whitespace-nowrap"
              data-testid="link-manage-ignored-locks"
            >
              Manage
            </Link>
          </div>
        )}
        {data.locks.length === 0 ? (
          <div className="border rounded-lg p-6 text-sm text-gray-500" data-testid="text-no-locks">
            No locks linked to this property yet.
          </div>
        ) : (
          data.locks.map((lock) => (
            <LockCard
              key={lock.deviceId}
              propertyId={propertyId}
              lock={lock}
              rooms={roomsQuery.data ?? []}
              isFlashing={flashingDeviceId === lock.deviceId}
            />
          ))
        )}
        {assignableLocks.length > 0 && (
          <div className="pt-2" data-testid="section-other-locks">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Other locks on your account
            </div>
            <div className="text-xs text-gray-500 mb-3">
              These locks aren’t auto-matched to this property. Pin one here
              if it belongs to this property or a room inside it.
            </div>
            {assignableLocks.map((lock) => (
              <AssignableLockCard
                key={lock.deviceId}
                propertyId={propertyId}
                lock={lock}
                rooms={roomsQuery.data ?? []}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Compact card for a lock that the fuzzy match didn't attach. Same
// PATCH endpoint, but the picker defaults to "Auto" with a hint
// telling the operator the current pin (if any) is for some other
// property/room — useful when a lock was wrongly pinned elsewhere.
function AssignableLockCard({
  propertyId,
  lock,
  rooms,
}: {
  propertyId: number;
  lock: AssignableLock;
  rooms: Room[];
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const name = lock.displayName || lock.deviceId.slice(0, 8);
  const pinHint =
    lock.pinnedPropertyId !== null
      ? "Currently pinned to a different property"
      : lock.pinnedRoomId !== null
      ? "Currently pinned to a different room"
      : null;

  const pinMutation = useMutation({
    mutationFn: async (value: string) => {
      let body: { propertyId: number | null; roomId: number | null };
      if (value === "property") body = { propertyId, roomId: null };
      else if (value.startsWith("room:")) body = { propertyId: null, roomId: parseInt(value.slice(5), 10) };
      else body = { propertyId: null, roomId: null };
      await apiRequest("PATCH", `/api/app/devices/seam/locks/${lock.deviceId}`, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "devices"] });
      toast({ title: "Lock assignment saved" });
    },
    onError: (err) => {
      toast({
        title: "Could not save lock assignment",
        description: parseErrorMessage(err, "Couldn't save the lock assignment. Please try again."),
        variant: "destructive",
      });
    },
  });

  return (
    <div
      className="border rounded-lg p-3 mb-2 bg-gray-50"
      data-testid={`card-assignable-lock-${lock.deviceId}`}
    >
      <div className="min-w-0 mb-2">
        <div
          className="font-medium text-sm text-gray-900 truncate"
          data-testid={`text-assignable-lock-name-${lock.deviceId}`}
        >
          {name}
        </div>
        {lock.location && (
          <div className="text-xs text-gray-500 truncate">{lock.location}</div>
        )}
        {pinHint && (
          <div className="text-xs text-amber-700 mt-1">{pinHint}</div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500 shrink-0">Pin to:</span>
        <Select
          value="auto"
          onValueChange={(v) => pinMutation.mutate(v)}
          disabled={pinMutation.isPending}
        >
          <SelectTrigger
            className="h-8 text-xs"
            data-testid={`select-assignable-lock-pin-${lock.deviceId}`}
          >
            <SelectValue placeholder="Choose…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="auto" data-testid={`option-assignable-pin-auto-${lock.deviceId}`}>
              Auto (don’t pin)
            </SelectItem>
            <SelectItem
              value="property"
              data-testid={`option-assignable-pin-property-${lock.deviceId}`}
            >
              This property (shared door)
            </SelectItem>
            {rooms.map((r) => (
              <SelectItem
                key={r.id}
                value={`room:${r.id}`}
                data-testid={`option-assignable-pin-room-${lock.deviceId}-${r.id}`}
              >
                {`Room ${r.roomNumber}`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

// Per-lock card with a Pin selector that lets the operator assign the
// lock to the whole property, to a specific room, or back to auto
// (legacy location-name match). Persists via the lock override PATCH.
function LockCard({
  propertyId,
  lock,
  rooms,
  isFlashing = false,
}: {
  propertyId: number;
  lock: PropertyLock;
  rooms: Room[];
  isFlashing?: boolean;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const name = lock.displayName || lock.deviceId.slice(0, 8);

  const currentValue: string =
    lock.pinSource === "property"
      ? "property"
      : lock.pinSource === "room" && lock.roomId !== null
      ? `room:${lock.roomId}`
      : "auto";

  // Task #1551: scroll into view and flash a highlight ring when this lock
  // card was deep-linked from a device health notification.
  const lockCardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isFlashing) return;
    lockCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [isFlashing]);

  const pinMutation = useMutation({
    mutationFn: async (value: string) => {
      let body: { propertyId: number | null; roomId: number | null };
      if (value === "property") body = { propertyId, roomId: null };
      else if (value.startsWith("room:")) body = { propertyId: null, roomId: parseInt(value.slice(5), 10) };
      else body = { propertyId: null, roomId: null };
      await apiRequest("PATCH", `/api/app/devices/seam/locks/${lock.deviceId}`, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "devices"] });
      toast({ title: "Lock assignment saved" });
    },
    onError: (err) => {
      toast({
        title: "Could not save lock assignment",
        description: parseErrorMessage(err, "Couldn't save the lock assignment. Please try again."),
        variant: "destructive",
      });
    },
  });

  const isTtlock = lock.provider === "ttlock";

  return (
    <div
      ref={lockCardRef}
      className={`border rounded-lg p-4 transition-all duration-500 ${isFlashing ? "ring-2 ring-brand ring-offset-2 bg-blue-50 dark:bg-blue-950/40" : ""}`}
      data-testid={`card-lock-${lock.deviceId}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-medium text-gray-900 truncate" data-testid={`text-lock-name-${lock.deviceId}`}>{name}</div>
            {isTtlock && (
              <span
                className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-medium text-orange-700 shrink-0"
                data-testid={`badge-provider-${lock.deviceId}`}
              >
                TTLock
              </span>
            )}
          </div>
          {lock.location && (
            <div className="text-xs text-gray-500 truncate">{lock.location}</div>
          )}
          <div
            className={`text-xs mt-1 ${lock.locked === null && lock.liveStatusSkipped ? "text-gray-400 italic" : "text-gray-500"}`}
            title={
              lock.locked === null && lock.liveStatusSkipped
                ? lock.lastUnlockAt
                  ? `Live status paused — no unlock activity in 7+ days (last seen ${new Date(lock.lastUnlockAt).toLocaleDateString()})`
                  : "Live status paused — no recorded unlock activity"
                : undefined
            }
            data-testid={`text-lock-status-${lock.deviceId}`}
          >
            {lock.locked === true
              ? "Locked"
              : lock.locked === false
              ? "Unlocked"
              : lock.liveStatusSkipped
              ? lock.lastUnlockAt
                ? `Last seen ${new Date(lock.lastUnlockAt).toLocaleDateString()}`
                : "Idle"
              : "Unknown"}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {lock.online ? (
            <Wifi className="h-4 w-4 text-green-600" />
          ) : (
            <WifiOff className="h-4 w-4 text-red-500" />
          )}
          {lock.batteryPct !== null && (
            <div
              className="flex items-center gap-1 text-xs text-gray-600"
              data-testid={`text-battery-${lock.deviceId}`}
            >
              <Battery className="h-3.5 w-3.5" />
              {lock.batteryPct}%
            </div>
          )}
        </div>
      </div>
      {isTtlock ? (
        <div className="mt-3 pt-3 border-t">
          <p className="text-xs text-gray-400">
            Assignment managed via TTLock settings.
          </p>
        </div>
      ) : (
        <div className="mt-3 pt-3 border-t flex items-center gap-2">
          <span className="text-xs text-gray-500 shrink-0">Pin to:</span>
          <Select
            value={currentValue}
            onValueChange={(v) => pinMutation.mutate(v)}
            disabled={pinMutation.isPending}
          >
            <SelectTrigger className="h-8 text-xs" data-testid={`select-lock-pin-${lock.deviceId}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto" data-testid={`option-pin-auto-${lock.deviceId}`}>
                Auto (match by name)
              </SelectItem>
              <SelectItem value="property" data-testid={`option-pin-property-${lock.deviceId}`}>
                This property (shared door)
              </SelectItem>
              {rooms.map((r) => (
                <SelectItem
                  key={r.id}
                  value={`room:${r.id}`}
                  data-testid={`option-pin-room-${lock.deviceId}-${r.id}`}
                >
                  {`Room ${r.roomNumber}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}

// Task #1201: each thermostat owns its own range state + readings query
// so widening the chart window only refetches that card. The endpoint
// returns readings keyed by deviceId for every thermostat on the
// property; React Query caches by `(propertyId, hours)` so multiple
// cards on the same window share a single network call.
// Exported for unit tests (client/src/__tests__/thermostat-card-range-selector.test.tsx).
export function ThermostatCard({
  propertyId,
  thermostat: t,
  notifications,
  isFlashing = false,
}: {
  propertyId: number;
  thermostat: PropertyThermostat;
  notifications: Array<{ type: string | null; link: string | null; read: boolean | null; createdAt: Date | string | null }>;
  isFlashing?: boolean;
}) {
  const [windowHours, setWindowHours] = useState<number>(24);
  const readingsQuery = useQuery<{
    days: number;
    hours: number;
    readings: Record<string, ThermostatReadingPoint[]>;
    lastSyncedAt?: Record<string, string | null>;
    latest?: Record<string, ThermostatLatestSnapshot | null>;
  }>({
    queryKey: ["/api/app/properties", propertyId, "thermostat-readings", { hours: windowHours }],
    queryFn: async () => {
      const res = await fetch(
        `/api/app/properties/${propertyId}/thermostat-readings?hours=${windowHours}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(`Failed to load thermostat readings (${res.status})`);
      return res.json();
    },
  });

  const name = t.customName || t.seamName || t.deviceId.slice(0, 8);
  const points = readingsQuery.data?.readings?.[t.deviceId] ?? [];
  const deviceLastSyncedAt = readingsQuery.data?.lastSyncedAt?.[t.deviceId] ?? null;
  const lastSyncedLabel = formatRelativeSync(deviceLastSyncedAt);
  // Task #1459: fetch a live temperature from Seam when the card opens so a
  // freshly-connected thermostat shows a current reading instead of "—" until
  // the next hourly poll. The cached reading still drives humidity, set
  // points and the chart; we just override the headline temp + show
  // "Last recorded X ago" for the cached value.
  const liveQuery = useQuery<{
    deviceId: string;
    live: { temperatureF: number | null; fetchedAt: string };
    cached: { temperatureF: number | null; recordedAt: string } | null;
  }>({
    queryKey: ["/api/app/devices/seam/thermostats", t.deviceId, "live"],
    queryFn: async () => {
      const res = await fetch(
        `/api/app/devices/seam/thermostats/${encodeURIComponent(t.deviceId)}/live`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(`Failed to load live thermostat reading (${res.status})`);
      return res.json();
    },
    staleTime: 60_000,
    retry: false,
  });
  // Prefer the stored snapshot (which carries humidity/setpoints) and
  // fall back to the live `devices` payload's temp/humidity so the card
  // still renders before the first stored reading lands.
  const snap = readingsQuery.data?.latest?.[t.deviceId] ?? null;
  const liveTempF = liveQuery.data?.live?.temperatureF ?? null;
  const cachedRecordedAt = liveQuery.data?.cached?.recordedAt ?? deviceLastSyncedAt;
  const cachedAgoLabel = formatRelativeSync(cachedRecordedAt);
  const tempF = liveTempF ?? snap?.temperatureF ?? t.temperatureF ?? null;
  const humidityPct = snap?.humidityPct ?? t.relativeHumidity ?? null;
  const heatF = snap?.heatSetPointF ?? t.heatSetPointF ?? null;
  const coolF = snap?.coolSetPointF ?? t.coolSetPointF ?? null;
  // Task #977: surface "Stuck for X" so an operator viewing a single
  // property can see at a glance whether one of the thermostat alerts
  // has been open for hours / days.
  const stuckSince = findOpenThermostatStuckSince(notifications, t.deviceId);
  const stuckLabel = stuckSince ? formatStuckDuration(stuckSince) : null;

  // Task #1551: scroll into view and flash a highlight ring when this card
  // was deep-linked from a device health notification.
  const cardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isFlashing) return;
    cardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [isFlashing]);

  return (
    <div
      ref={cardRef}
      className={`border rounded-lg p-4 transition-all duration-500 ${isFlashing ? "ring-2 ring-brand ring-offset-2 bg-blue-50 dark:bg-blue-950/40" : ""}`}
      data-testid={`card-thermostat-${t.deviceId}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="font-medium text-gray-900 truncate" data-testid={`text-name-${t.deviceId}`}>
              {name}
            </div>
            {stuckLabel && (
              <span
                className="shrink-0 inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 border border-orange-200"
                title={stuckSince ? `First detected ${stuckSince.toLocaleString()}` : undefined}
                aria-label={`Stuck for ${stuckLabel}`}
                data-testid={`thermostat-card-stuck-${t.deviceId}`}
              >
                Stuck for {stuckLabel}
              </span>
            )}
          </div>
          {t.location && <div className="text-xs text-gray-500 truncate">{t.location}</div>}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="flex flex-col items-end">
            <span
              className="text-2xl font-semibold text-gray-900 tabular-nums"
              data-testid={`text-temperature-${t.deviceId}`}
            >
              {tempF !== null ? `${Math.round(tempF)}°F` : "—"}
            </span>
            {liveTempF != null && (
              <span
                className="text-[10px] font-medium uppercase tracking-wide text-green-600"
                data-testid={`badge-live-${t.deviceId}`}
              >
                Live
              </span>
            )}
          </div>
          {t.online ? (
            <Wifi className="h-4 w-4 text-green-600" data-testid={`icon-online-${t.deviceId}`} />
          ) : (
            <WifiOff className="h-4 w-4 text-red-500" data-testid={`icon-offline-${t.deviceId}`} />
          )}
        </div>
      </div>
      {(humidityPct != null || heatF != null || coolF != null) && (
        <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
          <div className="rounded border border-gray-100 bg-gray-50 px-2 py-1.5" data-testid={`stat-humidity-${t.deviceId}`}>
            <div className="text-gray-500">Humidity</div>
            <div className="font-medium text-gray-900 tabular-nums">
              {humidityPct != null ? `${Math.round(humidityPct)}%` : "—"}
            </div>
          </div>
          <div className="rounded border border-gray-100 bg-gray-50 px-2 py-1.5" data-testid={`stat-heat-${t.deviceId}`}>
            <div className="text-gray-500">Low (Heat)</div>
            <div className="font-medium text-gray-900 tabular-nums">
              {heatF != null ? `${Math.round(heatF)}°F` : "—"}
            </div>
          </div>
          <div className="rounded border border-gray-100 bg-gray-50 px-2 py-1.5" data-testid={`stat-cool-${t.deviceId}`}>
            <div className="text-gray-500">High (Cool)</div>
            <div className="font-medium text-gray-900 tabular-nums">
              {coolF != null ? `${Math.round(coolF)}°F` : "—"}
            </div>
          </div>
        </div>
      )}
      <div
        className="flex items-center justify-end gap-1 mb-2"
        role="group"
        aria-label="Chart range"
        data-testid={`range-selector-${t.deviceId}`}
      >
        {THERMOSTAT_RANGE_OPTIONS.map((opt) => {
          const active = opt.hours === windowHours;
          return (
            <button
              key={opt.hours}
              type="button"
              onClick={() => setWindowHours(opt.hours)}
              aria-pressed={active}
              className={
                "text-[11px] px-2 py-0.5 rounded border transition-colors " +
                (active
                  ? "bg-brand text-white border-brand"
                  : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50")
              }
              data-testid={`button-range-${opt.label}-${t.deviceId}`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      <ThermostatTrendChart data={points} />
      {(cachedAgoLabel || lastSyncedLabel) && (
        <div
          className="mt-2 text-xs text-gray-500"
          title={cachedRecordedAt ? new Date(cachedRecordedAt).toLocaleString() : undefined}
          data-testid={`text-last-synced-${t.deviceId}`}
        >
          {liveTempF != null
            ? `Last recorded ${cachedAgoLabel ?? lastSyncedLabel}`
            : `Last updated ${cachedAgoLabel ?? lastSyncedLabel}`}
        </div>
      )}
    </div>
  );
}
